import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Mic, Square, MessageCircle, RefreshCw, Users, ChevronDown, ChevronUp, Clock, Lightbulb } from 'lucide-react';

function RecordButton({ testId, label }) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
    } catch { alert('Microphone non disponible.'); }
  };

  const stop = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!isRecording ? (
        <button onClick={start} className="flex items-center gap-1.5 text-xs text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg px-3 py-1.5 hover:bg-[#D4AF37]/5 transition-colors" data-testid={testId}>
          <Mic className="w-3 h-3" /> {label || 'Enregistrer'}
        </button>
      ) : (
        <button onClick={stop} className="flex items-center gap-1.5 text-xs text-red-500 border border-red-300 rounded-lg px-3 py-1.5 bg-red-50 animate-pulse" data-testid={`${testId}-stop`}>
          <Square className="w-3 h-3" /> Arrêter
        </button>
      )}
      {audioUrl && <audio controls src={audioUrl} className="h-8 max-w-[160px]" />}
    </div>
  );
}

function CollapsibleSection({ icon: Icon, title, accent, children, testId, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="rounded-xl border border-[#E8E4DC] overflow-hidden transition-all" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 bg-white hover:bg-[#FAFAF8] transition-colors text-left">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0`} style={{ background: `${accent}15`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm font-bold text-[#1A2F4A] flex-1" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 bg-white">{children}</div>}
    </div>
  );
}

const TEXTS = {
  A1: [
  {
    id: 'famille',
    theme: 'Ma famille et mes amis',
    title: 'Ma famille',
    missionExpressive: 'Lis le texte comme si tu racontais quelque chose de chaleureux à un ami.',
    texte: [
      "J'aime beaucoup ma famille.",
      "J'ai une petite famille qui vit près de chez moi.",
      "Il y a ma mère, mon père et un frère.",
      "Nous parlons souvent et nous dînons ensemble le soir.",
      "Le dimanche, nous faisons une promenade ensemble.",
      "J'aime beaucoup ma famille.",
    ],
    missionVariantes: [
      'avec joie',
      'calmement',
      'comme un souvenir heureux',
    ],
    reagis: [
      'Ta famille est grande ou petite ?',
      'Avec qui habites-tu ?',
      'Que fais-tu avec ta famille le week-end ?',
      'Aimes-tu les repas en famille ?',
      'Tu fais des promenades avec ta famille ?',
    ],
    transformations: [
      {
        phrase: "J'ai une petite famille.",
        instruction: 'Change "petite".',
        exemple: "J'ai une grande famille.",
      },
      {
        phrase: 'Nous dînons ensemble le soir.',
        instruction: 'Change le moment de la journée.',
        exemple: 'Nous dînons ensemble le midi.',
      },
      {
        phrase: 'Le dimanche, nous faisons une promenade.',
        instruction: "Change l'activité.",
        exemple: 'Le dimanche, nous regardons un film.',
      },
    ],
    dialogues: [
      { question: 'Tu aimes passer du temps avec ta famille ?', instruction: 'Réponds avec une phrase complète.' },
      { question: 'Que faites-vous ensemble le dimanche ?', instruction: 'Réponds librement.' },
      { question: 'Qui est la personne la plus drôle dans ta famille ?', instruction: 'Donne une réponse personnelle.' },
    ],
  },
  {
    id: 'ami',
    theme: 'Ma famille et mes amis',
    title: 'Mon meilleur ami',
    missionExpressive: "Lis le texte comme si tu parlais de ton meilleur ami avec enthousiasme.",
    texte: [
      "Mon meilleur ami s'appelle Ravi.",
      "Nous nous connaissons depuis l'enfance.",
      'Chaque semaine, nous jouons au foot dans le parc.',
      'Nous parlons de nos projets et de nos rêves.',
      'Il est très drôle et gentil.',
    ],
    missionVariantes: [],
    reagis: [
      'Comment s\'appelle ton meilleur ami ?',
      'Depuis quand êtes-vous amis ?',
      'Que faites-vous ensemble ?',
      'Ton ami est drôle ?',
      'De quoi parlez-vous ensemble ?',
    ],
    transformations: [
      {
        phrase: 'Nous jouons au foot dans le parc.',
        instruction: 'Change le sport.',
        exemple: 'Nous jouons au badminton dans le parc.',
      },
      {
        phrase: 'Il est très drôle et gentil.',
        instruction: 'Change la description.',
        exemple: 'Il est calme et intelligent.',
      },
    ],
    dialogues: [
      { question: "Qu'est-ce que tu aimes faire avec tes amis ?", instruction: 'Réponds librement.' },
      { question: 'Ton meilleur ami est comment ?', instruction: 'Décris ton ami avec 2 ou 3 adjectifs.' },
    ],
  },
  {
    id: 'maison',
    theme: 'Ma maison',
    title: 'Ma maison',
    missionExpressive: 'Lis le texte comme si tu décrivais un endroit paisible et personnel.',
    texte: [
      'Ma maison est petite mais confortable.',
      "Il y a une chambre, une cuisine et un salon.",
      'Le salon est lumineux et il y a des plantes.',
      'Ma chambre est mon espace préféré.',
      "Il y a un lit, une table, une chaise et une grande fenêtre.",
      "J'ai aussi des plantes et quelques livres.",
      'Le matin, la lumière entre dans la pièce et c\'est très joli.',
      'Je fais mon lit chaque jour pour garder la chambre propre.',
      "J'écoute souvent de la musique ici.",
      "C'est un endroit calme où je peux penser et rêver.",
      '',
      "Quand je rentre à la maison après le travail, j'aime me reposer dans ma chambre.",
      'Je change mes vêtements et je me repose un peu.',
      'Ensuite, je prépare le dîner avec ma mère.',
      "Nous aimons cuisiner ensemble, c'est amusant.",
      'Après le repas, je regarde une série à la télévision.',
      'Je parle un peu avec mes amis au téléphone.',
      "Avant de dormir, je lis un livre ou j'écoute de la musique douce.",
      'Puis je dors vers dix heures.',
      "C'est une soirée tranquille.",
    ],
    missionVariantes: [
      'chaleureusement',
      'calmement',
      'comme une soirée paisible',
    ],
    reagis: [
      'Ta maison est grande ou petite ?',
      'Quelle est ta pièce préférée ?',
      'Tu écoutes de la musique dans ta chambre ?',
      'Que fais-tu après le travail ou les cours ?',
      'Tu regardes des séries le soir ?',
      'Tu lis avant de dormir ?',
    ],
    transformations: [
      {
        phrase: 'Ma maison est petite mais confortable.',
        instruction: 'Change "petite" par un autre adjectif.',
        exemple: 'Ma maison est grande mais confortable.',
      },
      {
        phrase: "J'écoute souvent de la musique ici.",
        instruction: "Remplace l'activité.",
        exemple: 'Je dessine souvent ici.',
      },
      {
        phrase: 'Puis je dors vers dix heures.',
        instruction: "Change l'heure.",
        exemple: 'Puis je dors vers onze heures.',
      },
      {
        phrase: 'Je prépare le dîner avec ma mère.',
        instruction: 'Change le membre de la famille.',
        exemple: 'Je prépare le dîner avec mon frère.',
      },
      {
        phrase: "Avant de dormir, je lis un livre.",
        instruction: 'Remplace "un livre" par un autre passe-temps.',
        exemple: "Avant de dormir, j'écoute un podcast.",
      },
    ],
    dialogues: [
      { question: 'Que fais-tu quand tu rentres à la maison ?', instruction: 'Décris ta routine avec 2-3 phrases.' },
      { question: 'Comment est ta chambre ?', instruction: 'Décris ta chambre simplement.' },
      { question: 'Tu préfères lire ou écouter de la musique le soir ?', instruction: 'Donne ta préférence avec une raison.' },
    ],
    defiSujet1: 'ta chambre',
    defiSujet2: 'ta routine du soir',
  },
  {
    id: 'supermarche',
    theme: 'Au supermarché',
    title: 'Au supermarché',
    missionExpressive: 'Lis le texte joyeusement, comme une vraie conversation.',
    texte: [
      'Je vais au supermarché avec ma mère.',
      'Il y a beaucoup de fruits et de légumes frais.',
      'Nous prenons un chariot.',
      '',
      'La marchande est gentille et elle me dit toujours bonjour.',
      "J'aime choisir les produits moi-même.",
      "J'aime les pommes et les bananes.",
      '',
      'Nous achetons des fruits, du pain, du lait et du fromage.',
      '',
      'À la caisse, la dame sourit.',
      'Nous disons : « Merci, au revoir ! »',
      '',
      "Faire les courses, c'est amusant !",
      '',
      'Après les courses, nous revenons à la maison.',
    ],
    missionVariantes: [
      'joyeusement',
      'naturellement et avec fluidité',
      'comme une vraie conversation',
    ],
    reagis: [
      'Tu aimes faire les courses ?',
      'Quels fruits aimes-tu ?',
      'Tu vas au supermarché avec qui ?',
      'Tu préfères les supermarchés ou les petits magasins ?',
      'Que prends-tu souvent au supermarché ?',
    ],
    transformations: [
      {
        phrase: "J'aime les pommes et les bananes.",
        instruction: 'Remplace les fruits.',
        exemple: "J'aime les oranges et les fraises.",
      },
      {
        phrase: 'Je vais au supermarché avec ma mère.',
        instruction: 'Change le membre de la famille.',
        exemple: 'Je vais au supermarché avec mon père.',
      },
      {
        phrase: 'Nous achetons des fruits, du pain, du lait et du fromage.',
        instruction: 'Change les produits.',
        exemple: 'Nous achetons du riz, des œufs, du jus et du chocolat.',
      },
      {
        phrase: 'Je vais au supermarché.',
        instruction: 'Remplace le lieu.',
        exemple: 'Je vais au marché.',
      },
    ],
    dialogues: [
      { question: 'Bonjour ! Vous cherchez quelque chose ?', instruction: 'Réponds comme si tu étais au magasin.' },
      { question: 'Quels fruits aimez-vous ?', instruction: 'Donne tes préférences.' },
      { question: 'Vous aimez cuisiner ?', instruction: 'Réponds avec une phrase complète.' },
    ],
    defiSujet1: 'tes aliments préférés',
    defiSujet2: 'une expérience au supermarché',
  },
  {
    id: 'journee',
    title: 'Ma journée',
    missionExpressive: 'Lis le texte naturellement, comme si tu décrivais ta routine quotidienne.',
    texte: [
      'Je me lève à sept heures.',
      'Je prends une douche.',
      'Je bois un café et je mange du pain.',
      "Je vais à l'école à huit heures.",
      'Je rentre à la maison à quatre heures.',
      'Je fais mes devoirs.',
      '',
      'Le soir, je dîne avec ma famille.',
      'Je dors à dix heures.',
    ],
    missionVariantes: [
      'naturellement',
      'comme une routine tranquille',
      'calmement et avec fluidité',
    ],
    reagis: [
      'À quelle heure te lèves-tu ?',
      'Que manges-tu le matin ?',
      "Tu vas à l'école ou au travail ?",
      'Que fais-tu le soir ?',
      'À quelle heure dors-tu ?',
    ],
    transformations: [
      {
        phrase: 'Je me lève à sept heures.',
        instruction: "Change l'heure.",
        exemple: 'Je me lève à six heures.',
      },
      {
        phrase: 'Je bois un café et je mange du pain.',
        instruction: 'Remplace les aliments.',
        exemple: 'Je bois du thé et je mange des céréales.',
      },
      {
        phrase: "Je vais à l'école à huit heures.",
        instruction: 'Remplace le lieu.',
        exemple: 'Je vais au travail à huit heures.',
      },
      {
        phrase: 'Le soir, je dîne avec ma famille.',
        instruction: 'Remplace "famille" par un autre mot.',
        exemple: 'Le soir, je dîne avec mes amis.',
      },
    ],
    dialogues: [
      { question: 'Bonjour ! Tu te lèves tôt ?', instruction: 'Réponds naturellement.' },
      { question: "Que fais-tu après l'école ?", instruction: 'Décris ta routine.' },
      { question: 'Tu manges quoi au petit déjeuner ?', instruction: 'Donne des détails personnels.' },
    ],
    defiSujet1: 'ta routine quotidienne',
    defiSujet2: 'tes activités du matin',
  },
  {
    id: 'temps',
    title: 'Le temps',
    missionExpressive: 'Lis le texte joyeusement, comme si tu profitais du beau temps.',
    texte: [
      "Aujourd'hui, il fait beau.",
      'Le soleil brille.',
      'Il fait chaud et le ciel est bleu.',
      "J'aime le soleil.",
      '',
      'Mais parfois, il pleut.',
      'Quand il pleut, je reste à la maison.',
      "Je bois du thé et j'écoute de la musique.",
      '',
      "J'aime tous les temps !",
    ],
    missionVariantes: [
      'joyeusement',
      'comme si tu profitais du beau temps',
      'change d\'émotion entre le soleil et la pluie',
    ],
    reagis: [
      "Quel temps fait-il aujourd'hui chez toi ?",
      'Tu préfères le soleil ou la pluie ?',
      'Que fais-tu quand il pleut ?',
      'Tu aimes boire du thé ?',
      'Quelle est ta saison préférée ?',
    ],
    transformations: [
      {
        phrase: 'Il fait chaud et le ciel est bleu.',
        instruction: 'Change "chaud" par un autre adjectif météo.',
        exemple: 'Il fait froid et le ciel est gris.',
      },
      {
        phrase: "Je bois du thé et j'écoute de la musique.",
        instruction: 'Remplace la boisson.',
        exemple: "Je bois du chocolat chaud et j'écoute de la musique.",
      },
      {
        phrase: 'Quand il pleut, je reste à la maison.',
        instruction: "Remplace l'activité.",
        exemple: 'Quand il pleut, je lis un livre.',
      },
      {
        phrase: "J'aime le soleil.",
        instruction: 'Remplace par une autre condition météo.',
        exemple: "J'aime la neige.",
      },
    ],
    dialogues: [
      { question: 'Tu aimes le beau temps ?', instruction: 'Réponds avec une phrase complète.' },
      { question: 'Que fais-tu quand il pleut ?', instruction: 'Donne des détails personnels.' },
      { question: "Tu préfères l'été ou l'hiver ?", instruction: 'Explique ta préférence.' },
    ],
    defiSujet1: 'ton temps préféré',
    defiSujet2: 'ce que tu aimes faire quand il pleut',
  },
  ],
  A2: [
    {
      id: 'changer-vie',
      title: 'Changer de vie',
      level: 'A2',
      missionExpressive: 'Lis le texte comme si tu racontais une expérience importante de ta vie.',
      texte: [
        "Il y a deux ans, j'ai décidé de changer de vie.",
        "Avant, je travaillais dans une grande entreprise à Delhi.",
        "Je gagnais bien ma vie, mais j'étais stressée et fatiguée.",
        "Un jour, j'ai compris que je voulais faire quelque chose de plus créatif.",
        "Alors, j'ai quitté mon travail et je me suis inscrite à une école de musique.",
        "Au début, c'était difficile, car je ne connaissais personne.",
        "Mais petit à petit, j'ai rencontré des gens passionnés comme moi.",
        "J'ai commencé à enseigner le chant aux enfants et cela m'a rendue très heureuse.",
        "Maintenant, je vis simplement, mais je me sens libre.",
        "J'ai moins d'argent, mais plus de paix.",
        "Je crois qu'il faut toujours écouter son cœur pour trouver sa voie.",
      ],
      missionVariantes: [
        'comme un souvenir inspirant',
        'calmement et avec sincérité',
        'comme si tu donnais un conseil à un ami',
        'avec fierté',
        'avec émotion et gratitude',
      ],
      reagis: [
        'Pourquoi la personne voulait-elle changer de vie ?',
        'Est-ce que son ancienne vie était confortable ?',
        'Pourquoi était-elle stressée ?',
        "Qu'a-t-elle décidé de faire ?",
        'Est-ce que le changement était facile au début ?',
        'Comment sa vie a-t-elle changé maintenant ?',
        'Tu comprends cette décision ?',
        'Est-ce que tu aimerais faire un travail plus créatif ?',
        "Qu'est-ce qui est le plus important pour toi : l'argent ou la paix intérieure ?",
        'As-tu déjà voulu changer quelque chose dans ta vie ?',
      ],
      opinionsQuestions: [
        "Penses-tu qu'il est difficile de suivre ses rêves ?",
        "Pourquoi certaines personnes restent-elles dans un travail qu'elles n'aiment pas ?",
        "Est-ce qu'il faut prendre des risques pour être heureux ?",
        'Que signifie "réussir sa vie" pour toi ?',
        "Est-ce qu'un travail doit seulement donner de l'argent ?",
      ],
      transformations: [
        {
          phrase: 'Je travaillais dans une grande entreprise à Delhi.',
          instruction: 'Change la ville.',
          exemple: 'Je travaillais dans une entreprise à Mumbai.',
        },
        {
          phrase: "J'étais stressée et fatiguée.",
          instruction: "Change l'émotion.",
          exemple: "J'étais motivée et pleine d'énergie.",
        },
        {
          phrase: "Je me suis inscrite à une école de musique.",
          instruction: "Change l'activité.",
          exemple: "Je me suis inscrite à une école de photographie.",
        },
        {
          phrase: 'Je vis simplement, mais je me sens libre.',
          instruction: 'Change la fin de la phrase.',
          exemple: 'Je vis simplement, mais je suis très heureuse.',
        },
      ],
      dialogues: [
        { question: 'Tu pourrais quitter ton travail pour suivre une passion ?', instruction: 'Réponds librement et explique pourquoi.' },
        { question: "Qu'est-ce qui te rend vraiment heureuse dans la vie ?", instruction: 'Donne une réponse personnelle.' },
        { question: 'Tu préfères une vie confortable ou une vie passionnante ?', instruction: 'Explique ton choix.' },
      ],
      defiDuration: '30 secondes',
      defiSujet1: 'un changement important dans ta vie',
      defiSujet2: 'une décision difficile que tu as prise',
      defiSujet3: 'une passion qui a changé ton quotidien',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'finalement'],
    },
    {
      id: 'etranger',
      title: "Vivre à l'étranger",
      level: 'A2',
      missionExpressive: "Lis le texte comme si tu racontais une aventure importante et pleine de découvertes.",
      texte: [
        "L'année dernière, j'ai passé trois mois en France pour apprendre le français.",
        "J'étais à Montpellier, une ville magnifique au sud du pays.",
        "Au début, tout était nouveau : la nourriture, les rues, les gens, la langue.",
        "J'avais un peu peur de parler, mais les Français étaient patients et gentils.",
        "J'ai habité dans une colocation avec deux étudiantes espagnoles.",
        "Nous parlions en français tous les jours.",
        "J'ai découvert la cuisine française : les croissants, les quiches, les fromages.",
        "Le week-end, je visitais les villages autour de la ville.",
        "J'ai aussi participé à un festival de musique.",
        "Ce voyage a changé ma vision du monde.",
        "J'ai compris qu'on peut se sentir chez soi même loin de son pays.",
      ],
      missionVariantes: [
        'avec enthousiasme',
        'comme un souvenir précieux',
        "avec la curiosité d'un voyageur",
      ],
      reagis: [
        'Pourquoi la personne est-elle allée en France ?',
        'Comment était la ville de Montpellier ?',
        'Pourquoi avait-elle peur au début ?',
        "Avec qui habitait-elle ?",
        'Comment a-t-elle pratiqué le français ?',
        "Qu'a-t-elle découvert pendant son séjour ?",
        "Qu'est-ce qui a changé après ce voyage ?",
        "Est-ce que tu aimerais vivre à l'étranger ?",
        'Dans quel pays aimerais-tu vivre ?',
        "Est-ce qu'il est important de voyager pour apprendre une langue ?",
      ],
      opinionsQuestions: [
        'As-tu déjà voyagé seul(e) ?',
        'Est-ce que tu aimerais faire une colocation ?',
        'Quelle cuisine étrangère aimerais-tu découvrir ?',
        "Est-ce que tu trouves facile de parler une langue étrangère ?",
        "Qu'est-ce qu'on apprend quand on vit dans un autre pays ?",
      ],
      transformations: [
        {
          phrase: "J'ai passé trois mois en France.",
          instruction: 'Change le pays.',
          exemple: "J'ai passé trois mois au Canada.",
        },
        {
          phrase: 'Nous parlions en français tous les jours.',
          instruction: 'Change la langue.',
          exemple: 'Nous parlions en anglais tous les jours.',
        },
        {
          phrase: "J'ai participé à un festival de musique.",
          instruction: "Change l'événement.",
          exemple: "J'ai participé à une exposition d'art.",
        },
        {
          phrase: 'Ce voyage a changé ma vision du monde.',
          instruction: 'Change la fin de la phrase.',
          exemple: "Ce voyage m'a donné plus de confiance.",
        },
      ],
      dialogues: [
        { question: 'Tu aimerais vivre en France un jour ?', instruction: 'Réponds librement et donne des raisons.' },
        { question: "Qu'est-ce qui est difficile quand on vit à l'étranger ?", instruction: 'Explique avec quelques exemples.' },
        { question: 'Quelle expérience aimerais-tu vivre dans un autre pays ?', instruction: 'Parle de tes rêves ou de tes projets.' },
      ],
      defiDuration: '1 minute',
      defiSujet1: 'un changement important dans ta vie',
      defiSujet2: 'une expérience de voyage',
      defiSujet3: 'un rêve que tu aimerais réaliser',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'quand', 'finalement'],
    },
    {
      id: 'benevolat',
      title: 'Le bénévolat',
      level: 'A2',
      missionExpressive: 'Lis le texte comme si tu racontais une expérience importante et positive.',
      texte: [
        "Depuis quelques mois, je fais du bénévolat dans une association.",
        "Nous aidons des enfants à faire leurs devoirs après l'école.",
        "Chaque mercredi, je passe deux heures avec eux.",
        "Au début, certains étaient timides, mais maintenant ils me parlent beaucoup.",
        "Nous travaillons les mathématiques et la lecture, mais aussi le dessin.",
        "J'aime voir leurs progrès semaine après semaine.",
        "Le bénévolat me donne le sentiment d'être utile.",
        "Je rencontre aussi d'autres bénévoles très motivés.",
        "Ensemble, nous organisons parfois des sorties éducatives.",
        "Cette expérience m'a appris la patience, l'écoute et la générosité.",
        "Donner un peu de temps, c'est donner beaucoup de bonheur.",
      ],
      missionVariantes: [
        'avec enthousiasme',
        'comme un souvenir inspirant',
        'calmement et avec sincérité',
        'comme si tu voulais motiver quelqu\'un',
        'avec émotion et gratitude',
      ],
      reagis: [
        'Où fait-elle du bénévolat ?',
        'Qui aide-t-elle ?',
        'Que font-ils ensemble ?',
        'Comment étaient les enfants au début ?',
        'Pourquoi aime-t-elle cette activité ?',
        "Qu'a-t-elle appris grâce au bénévolat ?",
        'Pourquoi le bénévolat est-il important selon toi ?',
        'Aimerais-tu aider dans une association ?',
        'Quel type de bénévolat voudrais-tu faire ?',
        "Est-ce qu'aider les autres rend heureux ?",
      ],
      opinionsQuestions: [
        'Pourquoi certaines personnes font-elles du bénévolat ?',
        "Est-ce qu'il faut aider les autres même quand on est occupé ?",
        'Que peut-on apprendre grâce au bénévolat ?',
        'Est-ce que les écoles devraient organiser des activités solidaires ?',
        'Comment peut-on aider les gens dans la vie quotidienne ?',
      ],
      transformations: [
        {
          phrase: 'Nous aidons des enfants à faire leurs devoirs.',
          instruction: 'Change les personnes.',
          exemple: 'Nous aidons des personnes âgées.',
        },
        {
          phrase: 'Chaque mercredi, je passe deux heures avec eux.',
          instruction: 'Change le jour.',
          exemple: 'Chaque samedi, je passe deux heures avec eux.',
        },
        {
          phrase: 'Nous travaillons les mathématiques et la lecture.',
          instruction: 'Change les matières.',
          exemple: 'Nous travaillons les langues et les sciences.',
        },
        {
          phrase: "Cette expérience m'a appris la patience.",
          instruction: 'Change la qualité.',
          exemple: "Cette expérience m'a appris la confiance.",
        },
      ],
      dialogues: [
        { question: 'Tu aimerais faire du bénévolat un jour ?', instruction: 'Réponds librement et explique pourquoi.' },
        { question: 'Quelle activité peut aider les autres selon toi ?', instruction: 'Donne des exemples.' },
        { question: "Qu'est-ce qu'on ressent quand on aide quelqu'un ?", instruction: 'Réponds avec des émotions et des détails.' },
      ],
      defiDuration: '30 secondes',
      defiSujet1: 'une activité de bénévolat',
      defiSujet2: 'une expérience où tu as aidé quelqu\'un',
      defiSujet3: 'une activité qui te rend utile et heureuse',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'finalement'],
    },
    {
      id: 'sans-internet',
      title: 'Une journée sans Internet',
      level: 'A2',
      missionExpressive: 'Lis le texte comme si tu racontais une expérience calme et personnelle.',
      texte: [
        "Un jour, j'ai décidé de passer toute une journée sans Internet.",
        "Au début, c'était difficile.",
        "J'avais l'habitude de vérifier mon téléphone toutes les dix minutes.",
        "J'ai éteint mon portable et mon ordinateur.",
        "J'ai pris un livre que je n'avais jamais terminé.",
        "J'ai lu pendant une heure, puis j'ai fait un peu de ménage.",
        "Ensuite, je suis sortie marcher dans le parc.",
        "J'ai observé les arbres, les enfants qui jouaient, les gens qui riaient.",
        "Je me sentais plus calme.",
        "L'après-midi, j'ai cuisiné un plat nouveau.",
        "Le soir, j'ai parlé avec ma famille sans aucune distraction.",
        "À la fin de la journée, j'étais fière de moi.",
        "J'ai compris que la vie sans écran peut être douce et paisible.",
        "Maintenant, j'essaie de faire la même chose chaque dimanche.",
      ],
      missionVariantes: [
        'calmement',
        'comme un moment de paix',
        'avec fierté',
      ],
      reagis: [
        'Pourquoi cette journée était-elle difficile au début ?',
        "Qu'a-t-elle fait après avoir éteint son téléphone ?",
        'Où est-elle allée marcher ?',
        'Comment se sentait-elle dans le parc ?',
        "Qu'a-t-elle fait avec sa famille ?",
        'Pourquoi cette expérience était-elle importante ?',
        'Est-ce que tu passes beaucoup de temps sur Internet ?',
        'Pourrais-tu vivre une journée sans écran ?',
        'Quelles activités aimes-tu faire sans téléphone ?',
        "Est-ce qu'Internet est parfois trop présent dans nos vies ?",
      ],
      opinionsQuestions: [
        'Combien de temps passes-tu sur ton téléphone chaque jour ?',
        'Que fais-tu quand tu veux te détendre ?',
        'Aimes-tu lire des livres ?',
        'Préfères-tu les activités en ligne ou les activités dehors ?',
        'Que pourrais-tu faire pendant une journée sans Internet ?',
      ],
      transformations: [
        {
          phrase: "J'ai passé une journée sans Internet.",
          instruction: 'Change la durée.',
          exemple: "J'ai passé un week-end sans Internet.",
        },
        {
          phrase: "J'ai lu pendant une heure.",
          instruction: "Change l'activité.",
          exemple: "J'ai dessiné pendant une heure.",
        },
        {
          phrase: 'Je suis sortie marcher dans le parc.',
          instruction: 'Change le lieu.',
          exemple: 'Je suis allée marcher à la plage.',
        },
        {
          phrase: "J'ai cuisiné un plat nouveau.",
          instruction: "Change l'activité.",
          exemple: "J'ai essayé une nouvelle recette de dessert.",
        },
      ],
      dialogues: [
        { question: 'Tu pourrais passer une journée entière sans téléphone ?', instruction: 'Réponds librement et explique pourquoi.' },
        { question: "Qu'est-ce que tu aimes faire pour te détendre ?", instruction: 'Donne quelques exemples personnels.' },
        { question: 'Est-ce que les réseaux sociaux prennent trop de place aujourd\'hui ?', instruction: 'Donne ton opinion simplement.' },
      ],
      defiDuration: '30 secondes',
      defiSujet1: 'une journée sans téléphone',
      defiSujet2: 'une activité calme que tu aimes faire',
      defiSujet3: 'une habitude que tu voudrais changer dans ta vie',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'ensuite'],
    },
    {
      id: 'problemes-quotidien',
      title: 'Les problèmes du quotidien',
      level: 'A2',
      missionExpressive: "Lis le texte comme si tu racontais une expérience du quotidien à un ami.",
      texte: [
        "Dans la vie, il y a toujours de petits problèmes à résoudre.",
        "Par exemple, la semaine dernière, j'ai perdu mes clés !",
        "J'étais devant ma porte sans savoir quoi faire.",
        "Heureusement, ma voisine m'a aidée.",
        "Elle m'a proposé d'attendre chez elle pendant que le serrurier arrivait.",
        "Ce n'était pas très grave, mais j'étais un peu stressée.",
        "Un autre jour, ma machine à laver est tombée en panne.",
        "J'ai dû aller à la laverie du quartier.",
        "Là-bas, j'ai rencontré une dame très gentille, et nous avons discuté.",
        "Finalement, ces petits problèmes m'ont permis de faire de nouvelles rencontres.",
        "La vie n'est jamais parfaite, mais elle est pleine de surprises.",
      ],
      missionVariantes: [
        'avec humour',
        "comme un souvenir stressant",
        'calmement et avec sincérité',
        'avec optimisme',
        "comme si tu racontais une histoire drôle à un ami",
      ],
      reagis: [
        "Quel problème a-t-elle eu la semaine dernière ?",
        "Pourquoi était-elle stressée ?",
        "Comment la voisine l'a-t-elle aidée ?",
        "Que s'est-il passé avec la machine à laver ?",
        "Où est-elle allée ensuite ?",
        "Qui a-t-elle rencontré à la laverie ?",
        "Pourquoi ces problèmes sont-ils finalement devenus positifs ?",
        "As-tu déjà perdu quelque chose d'important ?",
        "Comment réagis-tu quand il y a un problème ?",
        "Est-ce que les petits problèmes peuvent parfois apporter quelque chose de positif ?",
      ],
      opinionsQuestions: [
        "Pourquoi les petits problèmes nous stressent-ils parfois beaucoup ?",
        "Est-ce qu'il est important d'aider ses voisins ?",
        "Comment peut-on rester calme dans une situation difficile ?",
        "Penses-tu que les problèmes peuvent nous apprendre quelque chose ?",
        "Pourquoi les rencontres inattendues sont-elles parfois intéressantes ?",
      ],
      transformations: [
        {
          phrase: "J'ai perdu mes clés.",
          instruction: "Change l'objet.",
          exemple: "J'ai perdu mon téléphone.",
        },
        {
          phrase: "Ma voisine m'a aidée.",
          instruction: 'Change la personne.',
          exemple: "Mon ami m'a aidée.",
        },
        {
          phrase: "Ma machine à laver est tombée en panne.",
          instruction: "Change l'objet.",
          exemple: "Mon ordinateur est tombé en panne.",
        },
        {
          phrase: "J'ai rencontré une dame très gentille.",
          instruction: 'Change la description.',
          exemple: "J'ai rencontré un homme très drôle.",
        },
      ],
      dialogues: [
        { question: 'Que fais-tu quand tu as un problème inattendu ?', instruction: 'Réponds librement et explique.' },
        { question: "As-tu déjà reçu l'aide d'un inconnu ?", instruction: 'Raconte une petite expérience.' },
        { question: 'Est-ce que les problèmes du quotidien peuvent nous rendre plus forts ?', instruction: 'Donne ton opinion.' },
      ],
      defiDuration: '30 secondes',
      defiSujet1: 'un petit problème que tu as eu',
      defiSujet2: 'une situation stressante du quotidien',
      defiSujet3: 'une rencontre inattendue dans ta vie',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'finalement'],
    },
    {
      id: 'transports',
      title: 'Les transports',
      level: 'A2',
      missionExpressive: 'Lis le texte comme si tu décrivais ta routine quotidienne.',
      texte: [
        "Chaque jour, je prends les transports pour aller au travail.",
        "J'utilise le bus ou le métro selon l'heure.",
        "Le matin, il y a beaucoup de monde et c'est parfois fatigant.",
        "Mais j'aime observer les gens : certains lisent, d'autres écoutent de la musique.",
        "Parfois, je rencontre des collègues et nous parlons un peu.",
        "Le soir, le trajet est plus calme.",
        "J'aime alors regarder le coucher du soleil depuis la fenêtre du bus.",
        "Les transports ne sont pas toujours pratiques, mais ils font partie de notre vie quotidienne.",
        "J'espère qu'un jour, il y aura plus de bus écologiques pour protéger la planète.",
      ],
      missionVariantes: [
        'comme une routine tranquille',
        'avec un ton fatigué le matin',
        'avec calme le soir',
        'avec espoir pour la fin',
      ],
      reagis: [
        'Quels transports utilise la personne ?',
        'Pourquoi le matin est-il fatigant ?',
        'Que font les gens dans les transports ?',
        'Que fait-elle pendant le trajet ?',
        'Comment est le trajet du soir ?',
        'Pourquoi aime-t-elle regarder par la fenêtre ?',
        "Que souhaite-t-elle pour l'avenir ?",
        'Quels transports utilises-tu souvent ?',
        'Préfères-tu le bus, le métro ou la voiture ?',
        'Est-ce que les transports dans ta ville sont pratiques ?',
      ],
      opinionsQuestions: [
        'Combien de temps passes-tu dans les transports chaque jour ?',
        'Que fais-tu pendant les trajets ?',
        'Aimes-tu voyager en transports publics ?',
        'Quels problèmes existe-t-il dans les transports ?',
        "Comment peut-on protéger l'environnement grâce aux transports ?",
      ],
      transformations: [
        {
          phrase: 'Je prends le bus pour aller au travail.',
          instruction: 'Change le moyen de transport.',
          exemple: 'Je prends le train pour aller au travail.',
        },
        {
          phrase: "Le matin, il y a beaucoup de monde.",
          instruction: 'Change le moment de la journée.',
          exemple: 'Le soir, il y a moins de monde.',
        },
        {
          phrase: 'Les gens écoutent de la musique.',
          instruction: "Change l'activité.",
          exemple: 'Les gens regardent leur téléphone.',
        },
        {
          phrase: "J'aime regarder le coucher du soleil.",
          instruction: 'Change ce que tu regardes.',
          exemple: "J'aime observer les rues de la ville.",
        },
      ],
      dialogues: [
        { question: 'Quel est le plus grand problème du quotidien pour toi ?', instruction: 'Réponds librement avec quelques détails.' },
        { question: 'Que fais-tu pendant les trajets ?', instruction: 'Décris tes habitudes.' },
        { question: 'Comment peut-on améliorer les transports dans les villes ?', instruction: 'Donne ton opinion simplement.' },
      ],
      defiDuration: '30 secondes',
      defiSujet1: 'tes transports quotidiens',
      defiSujet2: 'un trajet que tu aimes faire',
      defiSujet3: 'les transports dans ta ville',
      defiConnecteurs: ['parce que', 'mais', 'alors', 'ensuite'],
    },
  ],
  B1: [
    {
      id: 'vivre-ailleurs',
      title: 'Vivre ailleurs',
      level: 'B1',
      missionExpressive: 'Lis le texte comme un témoignage personnel, avec réflexion et sincérité.',
      texte: [
        "Vivre dans un autre pays, c'est une aventure extraordinaire.",
        "Quand on arrive, tout semble différent : les rues, les sons, les odeurs.",
        "Au début, on se sent un peu perdu, comme un enfant qui apprend tout.",
        "Il faut comprendre les règles, les habitudes et la culture.",
        "Parfois, les gens ne réagissent pas comme on l'imagine.",
        "Mais c'est justement cela qui rend l'expérience si enrichissante.",
        "",
        "J'ai appris à écouter avant de parler, à observer avant de juger.",
        "La langue était une vraie barrière au départ.",
        "Je faisais beaucoup d'erreurs, mais les gens étaient patients.",
        "Avec le temps, j'ai commencé à penser dans la langue du pays.",
        "C'est à ce moment-là que je me suis senti vraiment intégré.",
        "",
        "Vivre ailleurs, c'est aussi apprendre sur soi-même.",
        "On découvre sa capacité d'adaptation, sa force intérieure.",
        "On comprend mieux d'où l'on vient.",
        "Je garde des liens forts avec mon pays d'origine.",
        "Mais j'ai maintenant un deuxième chez-moi.",
        "Chaque culture m'a offert une partie de moi-même.",
        "Partir, c'est grandir.",
        "Revenir, c'est se connaître.",
        "Et parfois, on ne revient plus jamais vraiment.",
      ],
      missionVariantes: [
        'calmement, avec réflexion',
        'comme un souvenir profond',
        'avec émotion et sincérité',
        'comme si tu partageais ton expérience',
      ],
      reagis: [
        'Pourquoi la personne se sent-elle perdue au début ?',
        "Quelle idée du texte te semble la plus importante ?",
        "Qu'est-ce qui rend l'expérience enrichissante selon le texte ?",
        "Comment la personne a-t-elle surmonté la barrière de la langue ?",
        "Que signifie « penser dans la langue du pays » ?",
        "Pourquoi dit-on « on ne revient plus jamais vraiment » ?",
      ],
      opinionsQuestions: [
        "Aimerais-tu vivre dans un autre pays ? Pourquoi ?",
        "La langue est-elle la difficulté principale quand on vit ailleurs ?",
        "Qu'est-ce qu'on apprend sur soi-même en vivant à l'étranger ?",
        "Peut-on avoir deux « chez-soi » ?",
        "Qu'est-ce qui aide une personne à s'intégrer dans un nouveau pays ?",
      ],
      reformulation: {
        instruction: 'Explique le texte avec tes propres mots. Parle pendant 30 à 60 secondes.',
        starters: ['Selon moi…', 'Le texte montre que…', 'Au début…', 'Finalement…'],
      },
      vocabulaire: [
        { expression: 'se sentir perdu', context: "Quand on arrive, on se sent un peu perdu." },
        { expression: 'une vraie barrière', context: "La langue était une vraie barrière au départ." },
        { expression: 'sa force intérieure', context: "On découvre sa capacité d'adaptation, sa force intérieure." },
        { expression: "garder des liens forts", context: "Je garde des liens forts avec mon pays d'origine." },
        { expression: 'se sentir intégré', context: "C'est à ce moment-là que je me suis senti vraiment intégré." },
      ],
      transformations: [
        {
          phrase: "Au début, on se sent un peu perdu.",
          instruction: "Change l'émotion.",
          exemple: "Au début, on se sent curieux et excité.",
        },
        {
          phrase: "La langue était une vraie barrière.",
          instruction: "Change la difficulté.",
          exemple: "La nourriture était un vrai défi au départ.",
        },
        {
          phrase: "Partir, c'est grandir.",
          instruction: "Reformule avec tes propres mots.",
          exemple: "Voyager nous fait évoluer en tant que personne.",
        },
      ],
      dialogues: [
        { question: "Tu crois que la langue est la difficulté principale quand on vit ailleurs ?", instruction: "Développe ta réponse avec des exemples." },
        { question: "Qu'est-ce qui te manquerait le plus si tu vivais dans un autre pays ?", instruction: "Parle de tes habitudes, ta famille, ta culture." },
        { question: "Partir, c'est grandir — tu es d'accord ?", instruction: "Donne ton opinion avec une explication." },
      ],
      reflexionFinale: "Quelle expérience t'a le plus fait grandir dans ta vie ?",
      defiDuration: '1 minute',
      defiSujet1: "une expérience qui t'a changé",
      defiSujet2: "ce que tu aimerais découvrir dans un autre pays",
      defiSujet3: "ce que signifie « se sentir chez soi »",
      defiConnecteurs: ['selon moi', 'cependant', 'en effet', 'finalement', 'par exemple'],
    },
    {
      id: 'esprit-equipe',
      title: "Esprit d'équipe",
      level: 'B1',
      missionExpressive: "Lis le texte avec conviction, comme si tu partageais une leçon de vie.",
      texte: [
        "Travailler en équipe, c'est apprendre à écouter les autres.",
        "Ce n'est pas toujours facile, car chacun a sa manière de penser.",
        "Mais c'est justement cette diversité qui crée la richesse du groupe.",
        "Dans mon travail, nous devons souvent collaborer sur des projets.",
        "Certains préfèrent diriger, d'autres observer, d'autres exécuter.",
        "Quand chacun trouve sa place, l'harmonie s'installe.",
        "Je crois que le respect est la base de toute équipe.",
        "Sans confiance, rien ne fonctionne vraiment.",
        "Il faut aussi savoir reconnaître les erreurs.",
        "Et surtout, ne pas avoir peur de demander de l'aide.",
        "Quand on réussit ensemble, la joie est plus forte.",
        "Un projet partagé crée des liens durables.",
        "",
        "J'ai appris à mieux communiquer grâce à mon équipe.",
        "Avant, j'étais timide et réservée.",
        "Aujourd'hui, je parle plus facilement.",
        "L'équipe me pousse à me dépasser.",
        "Nous avons connu des échecs, mais aussi de belles réussites.",
        "Chaque expérience renforce notre cohésion.",
        "Travailler seul, c'est aller vite.",
        "Mais ensemble, on va plus loin.",
      ],
      missionVariantes: [
        'avec conviction',
        'comme un partage d\'expérience',
        'calmement et avec réflexion',
        'avec fierté',
      ],
      reagis: [
        "Pourquoi le travail en équipe est-il difficile selon le texte ?",
        "Quel est le rôle du respect dans une équipe ?",
        "Comment la personne a-t-elle évolué grâce à son équipe ?",
        "Que signifie « ensemble, on va plus loin » ?",
        "Pourquoi la confiance est-elle essentielle ?",
        "Qu'est-ce qu'on apprend des échecs en équipe ?",
      ],
      opinionsQuestions: [
        "Préfères-tu travailler seul(e) ou en équipe ? Pourquoi ?",
        "Quelles qualités sont importantes dans un bon coéquipier ?",
        "As-tu déjà eu une expérience d'équipe difficile ?",
        "Comment gères-tu les désaccords dans un groupe ?",
        "Le leadership est-il inné ou s'apprend-il ?",
      ],
      reformulation: {
        instruction: "Résume l'idée principale du texte avec tes propres mots. Parle pendant 30 à 60 secondes.",
        starters: ['Le texte explique que…', 'L\'auteur pense que…', 'Personnellement…', 'En résumé…'],
      },
      vocabulaire: [
        { expression: 'reconnaître ses erreurs', context: "Il faut aussi savoir reconnaître les erreurs." },
        { expression: 'créer des liens durables', context: "Un projet partagé crée des liens durables." },
        { expression: 'se dépasser', context: "L'équipe me pousse à me dépasser." },
        { expression: 'renforcer la cohésion', context: "Chaque expérience renforce notre cohésion." },
        { expression: 'dépasser sa timidité', context: "Avant, j'étais timide et réservée. Aujourd'hui, je parle plus facilement." },
      ],
      transformations: [
        {
          phrase: "Je crois que le respect est la base de toute équipe.",
          instruction: "Change la qualité.",
          exemple: "Je crois que la communication est la base de toute équipe.",
        },
        {
          phrase: "Travailler seul, c'est aller vite.",
          instruction: "Reformule avec une autre idée.",
          exemple: "Travailler seul, c'est être libre mais limité.",
        },
        {
          phrase: "Avant, j'étais timide et réservée.",
          instruction: "Change la description.",
          exemple: "Avant, j'étais silencieuse et prudente.",
        },
      ],
      dialogues: [
        { question: "Tu penses que le travail en équipe est toujours efficace ?", instruction: "Donne ton avis avec des exemples concrets." },
        { question: "Quel rôle prends-tu naturellement dans un groupe ?", instruction: "Décris ton style de collaboration." },
        { question: "Un projet partagé crée des liens — tu es d'accord ?", instruction: "Explique avec une expérience personnelle." },
      ],
      reflexionFinale: "Qu'est-ce qui fait la force d'une équipe selon toi ?",
      defiDuration: '1 minute',
      defiSujet1: "une expérience de travail en équipe",
      defiSujet2: "les qualités d'un bon leader",
      defiSujet3: "un moment où tu as dû collaborer avec quelqu'un de différent",
      defiConnecteurs: ['d\'une part', 'd\'autre part', 'en revanche', 'par exemple', 'en conclusion'],
    },
    {
      id: 'les-medias',
      title: 'Les médias',
      level: 'B1',
      missionExpressive: "Lis le texte avec un ton réfléchi et critique, comme si tu analysais la situation.",
      texte: [
        "Les médias occupent une place essentielle dans notre vie.",
        "Chaque matin, nous lisons des informations sur nos téléphones.",
        "Nous savons tout, tout de suite, partout dans le monde.",
        "Mais cette rapidité a aussi ses dangers.",
        "Les fausses nouvelles circulent facilement.",
        "Il devient difficile de distinguer le vrai du faux.",
        "Les journalistes ont une grande responsabilité.",
        "Ils doivent informer sans manipuler.",
        "Aujourd'hui, chacun peut devenir un « journaliste » sur Internet.",
        "Mais tout le monde n'a pas l'éthique professionnelle nécessaire.",
        "Les images peuvent être modifiées, les faits exagérés.",
        "Nous devons apprendre à vérifier les sources.",
        "Lire plusieurs points de vue est essentiel.",
        "Les médias façonnent notre perception du monde.",
        "Ils peuvent unir ou diviser selon leur usage.",
        "",
        "J'essaie de garder une distance critique.",
        "Je choisis mes sources avec attention.",
        "Je préfère les médias qui expliquent plutôt que ceux qui choquent.",
        "S'informer, c'est un acte de liberté.",
        "Mais c'est aussi une responsabilité personnelle.",
      ],
      missionVariantes: [
        'avec un ton réfléchi',
        'comme si tu donnais un discours',
        'avec conviction et clarté',
        'comme un journaliste critique',
      ],
      reagis: [
        "Pourquoi les médias sont-ils essentiels selon le texte ?",
        "Quels dangers sont mentionnés dans le texte ?",
        "Pourquoi est-il difficile de distinguer le vrai du faux ?",
        "Que signifie « garder une distance critique » ?",
        "Pourquoi s'informer est-il un acte de liberté ?",
        "Quelle est la responsabilité des journalistes ?",
      ],
      opinionsQuestions: [
        "Les médias influencent-ils notre façon de penser ?",
        "Fais-tu confiance aux informations que tu lis en ligne ?",
        "Comment vérifies-tu une information avant de la croire ?",
        "Les réseaux sociaux sont-ils de bons outils d'information ?",
        "Devrait-on apprendre à vérifier les sources à l'école ?",
      ],
      reformulation: {
        instruction: "Résume le message principal du texte. Quelle est la position de l'auteur ? Parle pendant 30 à 60 secondes.",
        starters: ["L'auteur affirme que…", 'Il souligne que…', 'Cependant…', 'En conclusion…'],
      },
      vocabulaire: [
        { expression: 'garder une distance critique', context: "J'essaie de garder une distance critique." },
        { expression: 'distinguer le vrai du faux', context: "Il devient difficile de distinguer le vrai du faux." },
        { expression: 'façonner la perception', context: "Les médias façonnent notre perception du monde." },
        { expression: 'vérifier les sources', context: "Nous devons apprendre à vérifier les sources." },
        { expression: 'un acte de liberté', context: "S'informer, c'est un acte de liberté." },
      ],
      transformations: [
        {
          phrase: "Les fausses nouvelles circulent facilement.",
          instruction: "Reformule avec tes propres mots.",
          exemple: "La désinformation se propage rapidement sur Internet.",
        },
        {
          phrase: "Je préfère les médias qui expliquent plutôt que ceux qui choquent.",
          instruction: "Change la préférence.",
          exemple: "Je préfère les médias qui analysent plutôt que ceux qui simplifient.",
        },
        {
          phrase: "S'informer, c'est un acte de liberté.",
          instruction: "Reformule l'idée différemment.",
          exemple: "Chercher la vérité est un droit fondamental.",
        },
      ],
      dialogues: [
        { question: "Tu crois que les gens vérifient les informations avant de les partager ?", instruction: "Donne ton avis avec des exemples." },
        { question: "Les réseaux sociaux sont-ils dangereux pour l'information ?", instruction: "Développe ton opinion." },
        { question: "Comment tu t'informes personnellement ?", instruction: "Décris tes habitudes médiatiques." },
      ],
      reflexionFinale: "Les médias nous rendent-ils plus libres ou plus manipulables ?",
      defiDuration: '1 minute',
      defiSujet1: "ton rapport avec les médias",
      defiSujet2: "un exemple de fausse information que tu as vu",
      defiSujet3: "comment devrait-on éduquer les jeunes aux médias",
      defiConnecteurs: ['en effet', 'cependant', 'par conséquent', 'il me semble que', 'en conclusion'],
    },
    {
      id: 'en-famille',
      title: 'En famille',
      level: 'B1',
      missionExpressive: "Lis le texte chaleureusement, comme si tu présentais ta famille à quelqu'un.",
      texte: [
        "Dans ma famille, nous sommes cinq personnes et chacun a son caractère.",
        "Mon père est calme et réfléchi, il aime lire le journal chaque matin.",
        "Ma mère est très active, elle cuisine, travaille et trouve encore le temps de rire.",
        "Mon frère adore les jeux vidéo et rêve de devenir informaticien.",
        "Ma sœur, plus jeune, veut être chanteuse et elle chante partout dans la maison.",
        "Nous avons aussi un chien, Oscar, qui fait partie de la famille.",
        "Le dimanche, nous déjeunons ensemble autour d'un grand repas.",
        "Ces moments sont remplis de conversations et de souvenirs.",
        "Parfois, nous discutons trop fort, mais c'est notre manière de vivre.",
        "Je crois que la famille, c'est un mélange d'amour et de patience.",
        "",
        "On ne choisit pas sa famille, mais on apprend à s'aimer malgré les différences.",
        "Quand j'étais enfant, je voulais souvent partir et vivre seul.",
        "Aujourd'hui, je comprends la valeur de ces liens.",
        "Ma famille m'a appris à écouter, à partager et à pardonner.",
        "Même si nous ne sommes pas toujours d'accord, nous restons proches.",
        "Quand l'un a un problème, les autres sont toujours là.",
        "C'est dans les moments difficiles que la famille montre sa force.",
        "Je pense que la famille est notre première école de vie.",
        "Elle nous apprend la tolérance, le respect et la solidarité.",
        "Sans elle, la vie perd un peu de sa chaleur.",
      ],
      missionVariantes: [
        'chaleureusement',
        'comme un souvenir tendre',
        'avec émotion et gratitude',
        'comme si tu parlais à un ami proche',
      ],
      reagis: [
        "Comment est la famille décrite dans le texte ?",
        "Quel est le caractère de chaque membre ?",
        "Que font-ils ensemble le dimanche ?",
        "Pourquoi la famille est-elle comparée à une « école de vie » ?",
        "Qu'a appris la personne grâce à sa famille ?",
        "Que signifie « on ne choisit pas sa famille » pour toi ?",
      ],
      opinionsQuestions: [
        "Les repas en famille sont-ils importants pour toi ?",
        "Comment gères-tu les désaccords dans ta famille ?",
        "Qu'est-ce que ta famille t'a appris de plus important ?",
        "La famille a-t-elle toujours la même importance aujourd'hui ?",
        "Quel rôle jouent les grands-parents dans une famille ?",
      ],
      reformulation: {
        instruction: "Présente ta propre famille avec tes mots. Parle pendant 30 à 60 secondes.",
        starters: ['Dans ma famille…', 'Ce que j\'aime le plus…', 'Nous avons l\'habitude de…', 'Pour moi, la famille…'],
      },
      vocabulaire: [
        { expression: 'un mélange d\'amour et de patience', context: "La famille, c'est un mélange d'amour et de patience." },
        { expression: 'malgré les différences', context: "On apprend à s'aimer malgré les différences." },
        { expression: 'la valeur de ces liens', context: "Aujourd'hui, je comprends la valeur de ces liens." },
        { expression: 'montrer sa force', context: "C'est dans les moments difficiles que la famille montre sa force." },
        { expression: 'une école de vie', context: "La famille est notre première école de vie." },
      ],
      transformations: [
        {
          phrase: "Mon père est calme et réfléchi.",
          instruction: "Décris ton propre père ou un membre de ta famille.",
          exemple: "Mon père est drôle et généreux, il aime raconter des histoires.",
        },
        {
          phrase: "Le dimanche, nous déjeunons ensemble.",
          instruction: "Change le jour ou l'activité.",
          exemple: "Le samedi, nous regardons un film ensemble.",
        },
        {
          phrase: "La famille est notre première école de vie.",
          instruction: "Reformule avec tes propres mots.",
          exemple: "C'est en famille qu'on apprend les valeurs essentielles.",
        },
      ],
      dialogues: [
        { question: "C'est quoi pour toi une famille unie ?", instruction: "Partage ta vision personnelle." },
        { question: "Quel est ton meilleur souvenir en famille ?", instruction: "Raconte un moment précis avec des détails." },
        { question: "La famille est-elle toujours importante dans le monde moderne ?", instruction: "Donne ton opinion avec des arguments." },
      ],
      reflexionFinale: "La famille est-elle notre plus grande force ou notre plus grande fragilité ?",
      defiDuration: '1 minute',
      defiSujet1: "ce que ta famille t'a appris",
      defiSujet2: "un souvenir de famille important",
      defiSujet3: "la place de la famille dans ta vie",
      defiConnecteurs: ['tout d\'abord', 'ensuite', 'en revanche', 'personnellement', 'en fin de compte'],
    },
    {
      id: 'communautes-durables',
      title: 'Communautés durables',
      level: 'B1',
      missionExpressive: "Lis le texte à voix haute avec un ton naturel, chaleureux et réfléchi. Imagine que tu partages une expérience inspirante dans une émission de société ou un podcast sur l'écologie et la vie urbaine. Fais attention aux pauses naturelles, au rythme des idées, à l'intonation et aux émotions transmises dans le texte.",
      texte: [
        "Les communautés durables représentent une nouvelle façon de vivre ensemble.",
        "Dans mon quartier, un groupe de voisins a créé un jardin partagé.",
        "Chacun apporte des graines, du temps et des idées.",
        "On y cultive des légumes, mais aussi des relations humaines.",
        "C'est beau de voir des enfants et des personnes âgées jardiner ensemble.",
        "Nous partageons aussi les outils, l'eau et même les repas du week-end.",
        "Ce projet a transformé notre manière de consommer et de penser.",
        "Nous faisons attention à nos déchets et à notre consommation d'énergie.",
        "Certaines familles utilisent des panneaux solaires.",
        "D'autres échangent leurs vêtements au lieu d'en acheter de nouveaux.",
        "Ce n'est pas seulement une question d'écologie, mais aussi de solidarité.",
        "On apprend que chacun peut faire une petite action utile.",
        "Et que, ensemble, ces petites actions deviennent puissantes.",
        "Je sens que la communauté donne du sens à la vie urbaine.",
        "Avant, je ne connaissais pas mes voisins, maintenant nous nous aidons.",
        "Nous avons aussi créé une bibliothèque libre dans la rue.",
        "Chacun peut y déposer ou prendre un livre gratuitement.",
        "Cela crée du lien et de la confiance.",
        "Vivre durablement, c'est aussi vivre humainement.",
        "C'est choisir la coopération plutôt que la compétition.",
      ],
      missionVariantes: [
        "comme un podcast inspirant sur l'écologie",
        "avec chaleur et émotion",
        "comme si tu partageais un témoignage personnel",
        "calmement, en valorisant chaque idée",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Vivre Ensemble & Écologie',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour des communautés durables et des nouvelles façons de vivre ensemble. Lis, analyse et réagis comme dans une vraie émission de société.",
        steps: [
          "analyser les idées principales",
          "exprimer ton opinion personnelle",
          "réagir aux initiatives présentées",
          "développer des arguments",
          "parler de ton expérience ou de ta société",
          "répondre comme dans une vraie discussion",
        ],
        objectif: "Développer une expression plus naturelle, fluide et nuancée en français tout en apprenant à discuter de sujets de société.",
        temps: 'Lecture : 1 à 2 min · Réaction & discussion : 3 à 5 min',
        conseil: "Parle comme si tu échangeais des idées avec de vrais auditeurs. Le plus important n'est pas d'être parfait, mais de développer tes idées, donner des exemples, exprimer une réflexion personnelle et parler avec naturel et spontanéité.",
      },
      speakingStarters: [
        "À mon avis…",
        "Je trouve cette idée intéressante parce que…",
        "Ce projet montre que…",
        "Il est vrai que…",
        "Aujourd'hui, beaucoup de personnes…",
        "Dans certaines villes…",
        "Pourtant…",
        "Cependant…",
        "Je pense que la solidarité…",
        "Cette initiative permet de…",
        "On peut constater que…",
        "Le plus important, c'est…",
        "Cela crée un vrai sentiment de…",
      ],
      reagis: [
        "Quel projet les voisins ont-ils créé dans le quartier ?",
        "Qu'est-ce qu'ils partagent au-delà du jardin ?",
        "En quoi ce projet a-t-il transformé leur mode de vie ?",
        "Quelles autres initiatives écologiques sont mentionnées ?",
        "Pourquoi la bibliothèque libre est-elle importante ?",
        "Que signifie « vivre durablement, c'est aussi vivre humainement » ?",
      ],
      opinionsQuestions: [
        "Existe-t-il des initiatives similaires dans ton quartier ou ta ville ?",
        "Penses-tu que les petites actions individuelles peuvent vraiment changer les choses ?",
        "Préfères-tu vivre dans une communauté soudée ou en gardant ton indépendance ?",
        "Comment peut-on encourager plus de solidarité entre voisins ?",
        "Es-tu prêt(e) à changer certaines habitudes pour l'écologie ?",
      ],
      reformulation: {
        instruction: "Résume le texte comme une chronique de podcast : présente l'initiative, ses bienfaits et ton opinion personnelle. Parle pendant 1 à 2 minutes.",
        starters: ["Dans cet épisode, j'ai envie de parler de…", "Ce que je retiens, c'est…", "L'idée principale est que…", "Personnellement, je pense que…"],
      },
      vocabulaire: [
        { expression: 'un jardin partagé', context: "Un groupe de voisins a créé un jardin partagé." },
        { expression: 'vivre ensemble', context: "Les communautés durables sont une nouvelle façon de vivre ensemble." },
        { expression: 'créer du lien', context: "La bibliothèque libre crée du lien et de la confiance." },
        { expression: "la consommation d'énergie", context: "Nous faisons attention à notre consommation d'énergie." },
        { expression: 'adopter un mode de vie durable', context: "Le projet aide à adopter un mode de vie durable." },
        { expression: 'partager les ressources', context: "Nous partageons les outils, l'eau et les repas." },
        { expression: 'la solidarité entre voisins', context: "Ce n'est pas seulement de l'écologie, c'est de la solidarité entre voisins." },
        { expression: 'une bibliothèque libre', context: "Nous avons créé une bibliothèque libre dans la rue." },
        { expression: 'faire une action utile', context: "Chacun peut faire une petite action utile." },
        { expression: 'choisir la coopération', context: "C'est choisir la coopération plutôt que la compétition." },
      ],
      transformations: [
        {
          phrase: "Un groupe de voisins a créé un jardin partagé.",
          instruction: "Change l'initiative collective.",
          exemple: "Un groupe de voisins a organisé un atelier de réparation.",
        },
        {
          phrase: "Nous partageons les outils, l'eau et même les repas du week-end.",
          instruction: "Change ce qui est partagé.",
          exemple: "Nous partageons les vélos, les livres et même nos compétences.",
        },
        {
          phrase: "Chacun peut faire une petite action utile.",
          instruction: "Reformule cette idée avec plus de conviction.",
          exemple: "Chaque geste compte, même le plus modeste, pour transformer la société.",
        },
      ],
      dialogues: [
        { question: "Aimerais-tu vivre dans une communauté comme celle du texte ?", instruction: "Explique tes raisons avec des exemples concrets." },
        { question: "Quelle action écologique pourrais-tu commencer dès demain ?", instruction: "Décris ton idée et son impact possible." },
        { question: "La solidarité entre voisins est-elle réaliste aujourd'hui ?", instruction: "Donne ton opinion et nuance ta réponse." },
      ],
      reflexionFinale: "Et si vivre ensemble autrement, c'était la vraie révolution silencieuse de notre époque ?",
      defiDuration: '1 minute',
      defiSujet1: "une initiative collective dans ta ville",
      defiSujet2: "un geste écologique que tu as adopté",
      defiSujet3: "la place de la solidarité dans ta vie",
      defiConnecteurs: ["d'une part", "d'autre part", "par exemple", "cependant", "en conclusion"],
    },
    {
      id: 'valoriser-candidature',
      title: 'Valoriser sa candidature',
      level: 'B1',
      missionExpressive: "Lis le texte à voix haute avec un ton naturel, sérieux et professionnel. Imagine que tu participes à un podcast où l'on parle de carrière, de motivation et d'expériences personnelles. Essaie de transmettre les idées avec une voix claire, des pauses naturelles, une intonation expressive et un rythme fluide et réfléchi.",
      texte: [
        "Chercher un emploi est toujours un moment important dans la vie.",
        "Il ne suffit pas d'avoir un diplôme, il faut savoir se présenter.",
        "Valoriser sa candidature, c'est raconter son parcours avec sincérité.",
        "Il faut parler de ses réussites, mais aussi de ses difficultés.",
        "Les recruteurs apprécient les personnes authentiques et motivées.",
        "Dans mon dernier entretien, j'ai expliqué pourquoi je voulais ce poste.",
        "J'ai montré que je connaissais bien l'entreprise et ses valeurs.",
        "J'ai aussi parlé de mes compétences relationnelles.",
        "Car dans le monde du travail, le savoir-être compte autant que le savoir-faire.",
        "Après l'entretien, j'ai envoyé un message de remerciement.",
        "Cette petite attention a fait une grande différence.",
        "Deux jours plus tard, j'ai reçu une réponse positive.",
        "J'ai compris que la préparation est la clé du succès.",
        "Il faut aussi savoir écouter et s'adapter.",
        "Parfois, une opportunité naît d'une simple conversation.",
        "Se valoriser, ce n'est pas se vanter, c'est se connaître.",
        "C'est savoir ce qu'on peut apporter à une équipe.",
        "Et c'est aussi reconnaître qu'on peut toujours apprendre.",
        "Je crois que chaque expérience, même un échec, a de la valeur.",
        "L'important, c'est de rester curieux et confiant.",
      ],
      missionVariantes: [
        "comme un podcast sur la vie professionnelle",
        "avec un ton sérieux et confiant",
        "comme si tu partageais un conseil important",
        "calmement, en valorisant chaque idée",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Travail & Développement Personnel',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour du monde du travail, des entretiens professionnels et de la confiance en soi. Lis, analyse et réagis comme dans une vraie émission de carrière.",
        steps: [
          "analyser les idées importantes du texte",
          "exprimer ton opinion",
          "parler de tes expériences personnelles",
          "développer des arguments",
          "réagir comme dans une vraie discussion",
          "réfléchir aux qualités professionnelles",
        ],
        objectif: "Développer une expression orale plus fluide, plus structurée et plus naturelle dans des situations liées au travail et à la vie professionnelle.",
        temps: 'Lecture : 1 à 2 min · Réaction & discussion : 3 à 5 min',
        conseil: "Imagine que tu partages ton expérience et tes idées dans une vraie discussion professionnelle. Le plus important est de développer tes réponses, expliquer clairement tes idées, donner des exemples personnels et parler avec confiance et naturel.",
      },
      speakingStarters: [
        "À mon avis…",
        "Je pense que…",
        "Dans le monde du travail…",
        "Il est important de…",
        "Selon moi…",
        "Cette expérience montre que…",
        "Pourtant…",
        "En réalité…",
        "Je suis convaincu(e) que…",
        "Le plus difficile, c'est…",
        "Ce qui compte vraiment, c'est…",
        "Une personne motivée doit…",
        "Je crois que la confiance en soi…",
      ],
      reagis: [
        "Pourquoi est-il important de savoir se présenter ?",
        "Que signifie « valoriser sa candidature » selon le texte ?",
        "Pourquoi les recruteurs aiment les candidats authentiques ?",
        "Comment la personne a-t-elle préparé son entretien ?",
        "Quel geste a fait la différence après l'entretien ?",
        "Que retient l'auteur de cette expérience ?",
      ],
      opinionsQuestions: [
        "Quelle est ta plus grande qualité professionnelle ?",
        "Comment te prépares-tu pour un entretien important ?",
        "Le savoir-être est-il plus important que le savoir-faire ?",
        "As-tu déjà vécu un échec qui t'a appris quelque chose ?",
        "Comment gères-tu le stress avant un entretien ?",
      ],
      reformulation: {
        instruction: "Résume le texte comme une chronique de podcast professionnel : présente le sujet, les conseils clés et ce que tu en retiens. Parle pendant 1 à 2 minutes.",
        starters: ["Dans cet épisode, on parle de…", "Ce que je retiens surtout, c'est…", "L'idée principale est que…", "Personnellement, je pense que…"],
      },
      vocabulaire: [
        { expression: 'valoriser sa candidature', context: "Valoriser sa candidature, c'est raconter son parcours avec sincérité." },
        { expression: 'compétences relationnelles', context: "J'ai parlé de mes compétences relationnelles." },
        { expression: 'savoir-être et savoir-faire', context: "Le savoir-être compte autant que le savoir-faire." },
        { expression: 'rester curieux', context: "L'important, c'est de rester curieux et confiant." },
        { expression: "s'adapter à une situation", context: "Il faut aussi savoir écouter et s'adapter." },
        { expression: 'reconnaître ses difficultés', context: "Il faut parler de ses réussites, mais aussi de ses difficultés." },
        { expression: 'envoyer un message de remerciement', context: "Après l'entretien, j'ai envoyé un message de remerciement." },
        { expression: 'se présenter avec sincérité', context: "Raconter son parcours avec sincérité fait la différence." },
        { expression: 'gagner en confiance', context: "Chaque expérience, même un échec, aide à gagner en confiance." },
        { expression: 'une opportunité professionnelle', context: "Parfois, une opportunité naît d'une simple conversation." },
      ],
      transformations: [
        {
          phrase: "J'ai expliqué pourquoi je voulais ce poste.",
          instruction: "Change la situation professionnelle.",
          exemple: "J'ai expliqué pourquoi je voulais rejoindre cette équipe.",
        },
        {
          phrase: "Le savoir-être compte autant que le savoir-faire.",
          instruction: "Reformule cette idée avec un exemple concret.",
          exemple: "Une personne agréable à travailler avec sera souvent préférée à un expert peu sociable.",
        },
        {
          phrase: "Chaque expérience, même un échec, a de la valeur.",
          instruction: "Développe cette idée avec une expérience personnelle.",
          exemple: "Un de mes échecs m'a appris à mieux préparer mes projets et à être plus humble.",
        },
      ],
      dialogues: [
        { question: "Quelle est la qualité la plus importante pour réussir un entretien ?", instruction: "Explique ta réponse avec un exemple personnel." },
        { question: "As-tu déjà reçu un conseil professionnel qui t'a marqué(e) ?", instruction: "Raconte la situation et son impact." },
        { question: "Peut-on apprendre la confiance en soi ?", instruction: "Donne ton opinion et nuance ta réponse." },
      ],
      reflexionFinale: "Et si la vraie réussite professionnelle, c'était d'apprendre à se connaître soi-même ?",
      defiDuration: '1 minute',
      defiSujet1: "un entretien important dans ta vie",
      defiSujet2: "une qualité professionnelle qui te définit",
      defiSujet3: "un conseil que tu donnerais à quelqu'un qui cherche un emploi",
      defiConnecteurs: ["tout d'abord", "ensuite", "par exemple", "cependant", "en conclusion"],
    },
  ],
  B2: [
    {
      id: 'numerique',
      title: 'La place du numérique dans nos vies',
      level: 'B2',
      missionExpressive: "Lis le texte à voix haute avec un ton naturel et réfléchi, comme dans un podcast ou une discussion sérieuse. Fais attention au rythme, aux pauses et à l'intonation pour transmettre les idées de manière claire et expressive.",
      texte: [
        "De nos jours, il est presque impossible d'imaginer une journée sans utiliser la technologie.",
        "Les réseaux sociaux, les smartphones et les plateformes de streaming font partie intégrante de notre quotidien.",
        "Cependant, cette dépendance a des conséquences notables sur nos relations humaines.",
        "En effet, bien que la communication soit plus rapide, elle devient aussi plus superficielle.",
        "Beaucoup de personnes préfèrent envoyer un message plutôt que de parler face à face.",
        "Cela provoque parfois un sentiment d'isolement ou de vide émotionnel.",
        "D'un autre côté, les outils numériques offrent des possibilités incroyables pour le travail et l'éducation.",
        "Grâce à Internet, on peut apprendre à distance, collaborer à l'international et développer sa créativité.",
        "Ainsi, la technologie n'est ni bonne ni mauvaise en soi : tout dépend de la manière dont on l'utilise.",
        "Il serait donc essentiel d'apprendre à l'utiliser avec modération et conscience.",
      ],
      missionVariantes: [
        'comme dans un podcast réfléchi',
        'avec un ton analytique et posé',
        'comme si tu présentais un point de vue nuancé',
        'avec conviction, en marquant les contrastes',
      ],
      b2Intro: {
        title: 'Lecture & Réflexion Critique',
        description: "Dans cette activité, tu vas lire, analyser et développer une réflexion personnelle autour d'un sujet de société.",
        steps: [
          'analyser les idées principales',
          'reformuler le texte avec tes propres mots',
          'exprimer ton opinion',
          'développer des arguments',
          'réagir aux questions',
          'défendre ou nuancer ton point de vue',
        ],
        objectif: "Développer une expression plus fluide, structurée et nuancée en français.",
        temps: 'Lecture : 1 à 2 min · Analyse et réaction orale : 2 à 4 min',
        conseil: "Ne cherche pas des phrases parfaites. Le plus important est de construire une réflexion personnelle, claire et naturelle. Explique tes idées, donne des exemples, nuance ton opinion et réagis spontanément.",
      },
      speakingStarters: [
        'À mon avis…',
        'Je pense que…',
        "D'un côté…, mais de l'autre…",
        'Il est vrai que…',
        'Pourtant…',
        'Cependant…',
        'En revanche…',
        'Cela montre que…',
        'On peut se demander si…',
        'Cette situation soulève une question importante…',
      ],
      reagis: [
        "Quelle est l'idée principale du texte ?",
        "Pourquoi l'auteur dit-il que la communication devient plus superficielle ?",
        "Quels sont les avantages du numérique mentionnés dans le texte ?",
        "Quels dangers ou inconvénients sont évoqués ?",
        "Que signifie « la technologie n'est ni bonne ni mauvaise en soi » ?",
        "L'auteur est-il plutôt optimiste ou pessimiste selon toi ?",
      ],
      opinionsQuestions: [
        "Penses-tu que la technologie nous rapproche ou nous éloigne les uns des autres ?",
        "Pourrais-tu vivre une semaine sans smartphone ? Pourquoi ?",
        "Les réseaux sociaux ont-ils plus d'avantages ou d'inconvénients ?",
        "Comment peut-on utiliser la technologie de manière plus responsable ?",
        "La dépendance au numérique est-elle un problème de société grave ?",
      ],
      reformulation: {
        instruction: "Résume les idées principales du texte avec tes propres mots. Présente la thèse de l'auteur, ses arguments et ta propre interprétation. Parle pendant 1 à 2 minutes.",
        starters: ["L'auteur soutient que…", 'D\'une part…, d\'autre part…', 'Ce qui me frappe, c\'est que…', 'En somme…'],
      },
      vocabulaire: [
        { expression: 'faire partie intégrante de', context: "Les réseaux sociaux font partie intégrante de notre quotidien." },
        { expression: 'un sentiment d\'isolement', context: "Cela provoque parfois un sentiment d'isolement." },
        { expression: 'un vide émotionnel', context: "Cela provoque un sentiment de vide émotionnel." },
        { expression: 'ni bon ni mauvais en soi', context: "La technologie n'est ni bonne ni mauvaise en soi." },
        { expression: 'avec modération et conscience', context: "Apprendre à l'utiliser avec modération et conscience." },
        { expression: 'des conséquences notables', context: "Cette dépendance a des conséquences notables." },
      ],
      transformations: [
        {
          phrase: "Beaucoup de personnes préfèrent envoyer un message plutôt que de parler face à face.",
          instruction: "Reformule cette idée avec un vocabulaire B2 différent.",
          exemple: "La communication écrite a largement remplacé les échanges en personne dans notre société.",
        },
        {
          phrase: "La technologie n'est ni bonne ni mauvaise en soi.",
          instruction: "Développe cette idée avec un argument personnel.",
          exemple: "La technologie est un outil neutre : c'est notre usage qui détermine son impact sur la société.",
        },
        {
          phrase: "Il serait essentiel d'apprendre à l'utiliser avec modération.",
          instruction: "Propose une solution concrète.",
          exemple: "Il faudrait intégrer l'éducation numérique dans les programmes scolaires dès le collège.",
        },
      ],
      dialogues: [
        { question: "Tu penses que les jeunes sont plus touchés par cette dépendance numérique que les adultes ?", instruction: "Développe ta réponse avec des exemples et des nuances." },
        { question: "Comment pourrait-on réduire l'impact négatif des écrans sur les relations humaines ?", instruction: "Propose des solutions concrètes et argumentées." },
        { question: "La technologie a-t-elle changé ta manière de communiquer avec tes proches ?", instruction: "Partage ton expérience personnelle avec des détails." },
      ],
      reflexionFinale: "Dans un monde de plus en plus connecté, est-il encore possible de vivre des relations authentiques ?",
      defiDuration: '2 minutes',
      defiSujet1: "la place du numérique dans ta vie quotidienne",
      defiSujet2: "les avantages et inconvénients de la technologie",
      defiSujet3: "comment trouver un équilibre entre vie numérique et vie réelle",
      defiConnecteurs: ['tout d\'abord', 'd\'un côté… de l\'autre', 'cependant', 'il me semble que', 'en définitive'],
    },
    {
      id: 'surconsommation',
      title: 'La surconsommation : un danger pour la planète',
      level: 'B2',
      missionExpressive: "Lis le texte comme si tu animais un épisode de podcast engagé sur la société et l'environnement. Adopte un ton posé mais convaincu, marque les pauses entre les idées, et laisse respirer les phrases pour que ton auditeur ait le temps de réfléchir.",
      texte: [
        "Aujourd'hui, nous vivons dans une société où acheter est devenu un réflexe presque automatique.",
        "Les vitrines, les publicités et les réseaux sociaux nous poussent sans cesse à consommer toujours plus, et toujours plus vite.",
        "Pourtant, derrière chaque produit se cachent des ressources naturelles, de l'énergie et souvent de la souffrance humaine.",
        "La surconsommation épuise la planète, accélère le réchauffement climatique et creuse les inégalités à l'échelle mondiale.",
        "Le plus paradoxal, c'est que cette accumulation d'objets ne nous rend pas véritablement plus heureux.",
        "Au contraire, elle entretient un sentiment permanent de manque, comme si le bonheur se trouvait toujours dans le prochain achat.",
        "Face à ce constat, de plus en plus de citoyens choisissent de consommer autrement : acheter moins, mais mieux.",
        "Ils privilégient la qualité, le local, la seconde main, ou tout simplement la sobriété.",
        "Changer nos habitudes n'est pas une privation, c'est une forme de liberté retrouvée.",
        "Repenser notre rapport à la consommation, c'est peut-être l'un des actes les plus puissants pour protéger notre avenir commun.",
      ],
      missionVariantes: [
        'comme un animateur de podcast engagé',
        'avec un ton journalistique et nuancé',
        'comme si tu lançais un débat de société',
        'avec gravité, en marquant les enjeux',
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Société & Réflexion',
        description: "Tu n'es plus seulement un apprenant : tu deviens la voix d'un épisode de podcast. Lis, analyse et réagis comme un commentateur qui partage une réflexion engagée avec son audience.",
        steps: [
          'incarner la voix d\'un podcast',
          'analyser les enjeux sociétaux',
          'reformuler avec ton propre regard',
          'défendre une position nuancée',
          'illustrer avec des exemples concrets',
          'conclure avec un message fort',
        ],
        objectif: "Travailler une expression orale dense, structurée, et capable de porter une opinion engagée à voix haute.",
        temps: 'Lecture : 1 à 2 min · Réaction & analyse orale : 3 à 5 min',
        conseil: "Imagine un auditeur à l'autre bout du micro. Parle pour quelqu'un, pas seulement pour t'entraîner. Ralentis, accentue les mots-clés, et laisse tes silences faire passer les idées importantes.",
      },
      speakingStarters: [
        "Bienvenue dans cet épisode…",
        "Aujourd'hui, j'aimerais qu'on prenne un instant pour…",
        "Posons-nous une vraie question…",
        "Le constat est simple…",
        "Ce qui me frappe, c'est que…",
        "On pourrait croire que…, et pourtant…",
        "Il est important de nuancer…",
        "Concrètement, cela signifie que…",
        "Imaginez un instant…",
        "Avant de refermer cet épisode, retenez ceci…",
      ],
      reagis: [
        "Quel est le message central de ce texte ?",
        "Pourquoi l'auteur parle-t-il d'un « réflexe presque automatique » ?",
        "Quelles conséquences de la surconsommation sont évoquées ?",
        "Que signifie l'idée que l'accumulation ne rend pas plus heureux ?",
        "Que propose le texte comme alternative à la surconsommation ?",
        "Pourquoi changer ses habitudes est-il présenté comme « une forme de liberté » ?",
      ],
      opinionsQuestions: [
        "Te considères-tu comme quelqu'un qui consomme beaucoup ou peu ? Pourquoi ?",
        "Penses-tu que la publicité a une influence réelle sur tes choix quotidiens ?",
        "Acheter d'occasion ou réparer ses objets : est-ce une solution efficace selon toi ?",
        "Est-ce la responsabilité des individus ou des entreprises de changer ce système ?",
        "Peut-on être heureux dans une société qui valorise autant la possession ?",
      ],
      reformulation: {
        instruction: "Reformule le texte comme une chronique de podcast. Présente le problème, les conséquences, et la solution proposée, en intégrant ton propre regard. Parle pendant 2 à 3 minutes.",
        starters: ["Dans cet épisode, on s'intéresse à…", "Le paradoxe, c'est que…", "On observe aujourd'hui que…", "Ce que je retiens surtout, c'est…"],
      },
      vocabulaire: [
        { expression: 'un réflexe automatique', context: "Acheter est devenu un réflexe presque automatique." },
        { expression: 'épuiser les ressources', context: "La surconsommation épuise la planète." },
        { expression: 'creuser les inégalités', context: "Elle creuse les inégalités à l'échelle mondiale." },
        { expression: 'un sentiment permanent de manque', context: "Cela entretient un sentiment permanent de manque." },
        { expression: 'consommer autrement', context: "De plus en plus de citoyens choisissent de consommer autrement." },
        { expression: 'la seconde main', context: "Ils privilégient le local, la seconde main, ou la sobriété." },
        { expression: 'une liberté retrouvée', context: "Changer nos habitudes, c'est une forme de liberté retrouvée." },
        { expression: 'notre avenir commun', context: "Un des actes les plus puissants pour protéger notre avenir commun." },
      ],
      transformations: [
        {
          phrase: "Acheter est devenu un réflexe presque automatique.",
          instruction: "Reformule cette idée avec un vocabulaire plus critique.",
          exemple: "La consommation s'est imposée comme un geste machinal, presque inconscient, dans nos vies quotidiennes.",
        },
        {
          phrase: "Cette accumulation d'objets ne nous rend pas véritablement plus heureux.",
          instruction: "Développe cette idée avec un argument personnel.",
          exemple: "Plus on possède, plus on désire : le bonheur semble toujours reculer d'un cran à chaque nouvel achat.",
        },
        {
          phrase: "Acheter moins, mais mieux.",
          instruction: "Transforme ce slogan en une véritable prise de position.",
          exemple: "Choisir un produit durable, c'est refuser une logique d'usage jetable et affirmer une autre vision du progrès.",
        },
        {
          phrase: "Changer nos habitudes n'est pas une privation, c'est une forme de liberté retrouvée.",
          instruction: "Propose une reformulation engagée et personnelle.",
          exemple: "Se libérer du superflu, c'est reprendre le contrôle de son temps, de son argent et de ses désirs.",
        },
      ],
      dialogues: [
        { question: "Si tu invitais un auditeur dans ton podcast, que lui dirais-tu pour le convaincre de consommer moins ?", instruction: "Adopte un ton direct, donne au moins deux arguments concrets." },
        { question: "Penses-tu que les jeunes générations sont vraiment plus engagées sur ces questions, ou est-ce un mythe ?", instruction: "Nuance ta réponse avec des exemples et des contre-exemples." },
        { question: "Comment réagirais-tu face à un proche qui te dirait : « De toute façon, ce que je fais ne changera rien » ?", instruction: "Construis une réponse argumentée, empathique mais ferme." },
      ],
      reflexionFinale: "Et si consommer moins, c'était finalement la manière la plus moderne de vivre mieux ?",
      defiDuration: '3 minutes',
      defiSujet1: "ta vision personnelle de la surconsommation",
      defiSujet2: "un geste concret que tu pourrais changer dans ta vie",
      defiSujet3: "le rôle des entreprises et des citoyens face à l'urgence écologique",
      defiConnecteurs: ['pour commencer', 'concrètement', 'd\'un côté… de l\'autre', 'cela dit', 'en définitive'],
    },
    {
      id: 'sante-mentale',
      title: 'La santé mentale : une crise silencieuse de notre époque',
      level: 'B2',
      missionExpressive: "Lis le texte à voix haute avec un ton calme, réfléchi et engagé. Imagine que tu participes à une émission où l'on échange des idées profondes sur le bien-être psychologique, la pression sociale et l'équilibre émotionnel. Marque les idées importantes, utilise des pauses naturelles et transmets les émotions du texte avec fluidité et nuance.",
      texte: [
        "Malgré les avancées considérables de la médecine moderne, la santé mentale demeure un sujet encore largement tabou dans de nombreuses sociétés.",
        "Beaucoup de personnes souffrent en silence, incapables d'exprimer leurs angoisses ou leur épuisement par peur du jugement ou de l'incompréhension.",
        "Le stress chronique, l'anxiété, la dépression ou encore le burn-out touchent aujourd'hui des individus de tous âges et de tous milieux sociaux.",
        "Cette réalité reflète un mode de vie marqué par la pression constante, la performance et l'hyperconnexion.",
        "Dans un monde où il faut toujours aller plus vite et réussir davantage, prendre soin de son équilibre psychologique devient pourtant essentiel.",
        "Parler de sa souffrance ne devrait pas être perçu comme un signe de faiblesse, mais comme une preuve de lucidité et de maturité émotionnelle.",
        "Les entreprises, les écoles et même les familles ont un rôle important à jouer dans cette prise de conscience collective.",
        "Favoriser l'écoute, le dialogue et la bienveillance pourrait contribuer à construire une société plus humaine et plus équilibrée.",
        "En fin de compte, la santé mentale ne concerne pas seulement l'individu : elle représente un véritable enjeu social et humain.",
      ],
      missionVariantes: [
        "comme un podcast engagé sur la société",
        "avec un ton posé et empathique",
        "comme si tu lançais un débat de société",
        "avec gravité, en marquant les enjeux humains",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Société & Santé mentale',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour d'un sujet sensible : la santé mentale dans le monde moderne. Lis, analyse et réagis comme dans une vraie émission de société.",
        steps: [
          "analyser les idées du texte",
          "exprimer ton point de vue",
          "discuter des causes et des conséquences",
          "défendre ou nuancer ton opinion",
          "réagir aux arguments comme dans une discussion",
          "faire des liens avec la société actuelle",
        ],
        objectif: "Développer une expression orale plus nuancée, plus naturelle et plus analytique en français.",
        temps: 'Lecture : 1 à 2 min · Analyse & discussion : 4 à 6 min',
        conseil: "Ne cherche pas seulement à parler correctement. Essaie surtout de développer une réflexion personnelle, nuancée et authentique. Laisse tes silences souligner les idées importantes.",
      },
      speakingStarters: [
        "À mon sens…",
        "Cette situation révèle que…",
        "On peut se demander si…",
        "Le véritable problème réside dans…",
        "Il est important de nuancer…",
        "Pourtant…",
        "En revanche…",
        "Cette pression sociale conduit souvent à…",
        "Il ne faut pas oublier que…",
        "Cela soulève une question essentielle…",
        "D'un point de vue psychologique…",
        "Cette problématique touche particulièrement…",
      ],
      reagis: [
        "Quelle est l'idée centrale du texte ?",
        "Pourquoi la santé mentale reste-t-elle un sujet tabou ?",
        "Quels troubles sont évoqués dans le texte ?",
        "Quel mode de vie est mis en cause ?",
        "Pourquoi parler de sa souffrance est-il présenté comme une force ?",
        "Quel rôle peuvent jouer les entreprises, écoles et familles ?",
      ],
      opinionsQuestions: [
        "Penses-tu que la société d'aujourd'hui prend assez au sérieux la santé mentale ?",
        "L'hyperconnexion est-elle vraiment un facteur de stress dans ta vie ?",
        "Pourquoi est-il encore difficile de parler de sa souffrance psychologique ?",
        "Quel rôle devraient avoir les entreprises dans le bien-être de leurs employés ?",
        "Que peut-on faire au quotidien pour préserver son équilibre psychologique ?",
      ],
      reformulation: {
        instruction: "Reformule le texte comme une chronique de podcast engagé. Présente le constat, les causes, les conséquences et propose une réflexion personnelle. Parle pendant 2 à 3 minutes.",
        starters: ["Dans cet épisode, on s'intéresse à…", "Le constat est sans appel…", "Ce que je retiens surtout, c'est…", "Au fond, la vraie question est…"],
      },
      vocabulaire: [
        { expression: 'souffrir en silence', context: "Beaucoup de personnes souffrent en silence, incapables d'exprimer leurs angoisses." },
        { expression: 'la pression sociale', context: "Cette pression sociale conduit souvent à l'épuisement." },
        { expression: "l'équilibre psychologique", context: "Prendre soin de son équilibre psychologique devient essentiel." },
        { expression: 'un sujet tabou', context: "La santé mentale demeure un sujet largement tabou." },
        { expression: 'favoriser le dialogue', context: "Favoriser l'écoute et le dialogue est essentiel." },
        { expression: 'prendre soin de son bien-être', context: "Il faut apprendre à prendre soin de son bien-être." },
        { expression: 'une société hyperconnectée', context: "Nous vivons dans une société hyperconnectée." },
        { expression: 'la maturité émotionnelle', context: "Parler de sa souffrance est une preuve de maturité émotionnelle." },
        { expression: 'un enjeu humain et social', context: "La santé mentale représente un véritable enjeu humain et social." },
      ],
      transformations: [
        {
          phrase: "Beaucoup de personnes souffrent en silence.",
          instruction: "Reformule cette idée avec un vocabulaire B2 plus critique.",
          exemple: "Un nombre croissant d'individus traversent une détresse invisible, faute de pouvoir l'exprimer ouvertement.",
        },
        {
          phrase: "Parler de sa souffrance ne devrait pas être perçu comme un signe de faiblesse.",
          instruction: "Développe cette idée avec une nuance personnelle.",
          exemple: "Au contraire, oser nommer sa fragilité demande souvent plus de courage que de continuer à tout cacher.",
        },
        {
          phrase: "Favoriser l'écoute, le dialogue et la bienveillance pourrait contribuer à construire une société plus humaine.",
          instruction: "Propose une solution concrète et engagée.",
          exemple: "Mettre en place des espaces de parole dans les entreprises et les écoles permettrait de normaliser cette discussion essentielle.",
        },
      ],
      dialogues: [
        { question: "Penses-tu que ta génération vit plus mal cette pression psychologique que les précédentes ?", instruction: "Nuance ta réponse avec des exemples concrets." },
        { question: "Comment réagirais-tu si un proche t'avouait souffrir de burn-out ?", instruction: "Construis une réponse empathique et argumentée." },
        { question: "Selon toi, la santé mentale devrait-elle être enseignée à l'école ?", instruction: "Défends ta position avec des arguments structurés." },
      ],
      reflexionFinale: "Et si prendre soin de son esprit devenait, demain, aussi naturel que prendre soin de son corps ?",
      defiDuration: '3 minutes',
      defiSujet1: "ta vision de la santé mentale aujourd'hui",
      defiSujet2: "un changement concret que la société devrait opérer",
      defiSujet3: "le lien entre hyperconnexion et fragilité psychologique",
      defiConnecteurs: ['pour commencer', 'cela dit', "d'un côté… de l'autre", 'par ailleurs', 'en définitive'],
    },
    {
      id: 'education',
      title: "L'éducation : un outil de liberté et de transformation sociale",
      level: 'B2',
      missionExpressive: "Lis le texte à voix haute avec un ton sérieux, clair et réfléchi. Imagine que tu participes à un débat sur les inégalités, les valeurs sociales et l'avenir des nouvelles générations. Mets en valeur les idées importantes, parle avec fluidité et transmets les nuances du texte.",
      texte: [
        "L'éducation ne se limite pas à l'apprentissage de connaissances académiques.",
        "Elle représente avant tout un moyen de former des citoyens capables de réfléchir, de comprendre le monde et d'agir de manière responsable.",
        "Dans une société en constante évolution, l'accès à une éducation de qualité devient essentiel pour réduire les inégalités et favoriser la cohésion sociale.",
        "Pourtant, dans de nombreuses régions du monde, l'école reste inaccessible à une partie de la population.",
        "Au-delà des compétences techniques, l'éducation transmet également des valeurs fondamentales telles que le respect, la tolérance et l'esprit critique.",
        "Elle permet de développer l'autonomie intellectuelle et de lutter contre l'ignorance, les discriminations et la manipulation.",
        "Investir dans l'éducation, c'est donc investir dans l'avenir d'une société plus juste et plus consciente.",
        "Une population éduquée est davantage capable de participer activement à la vie démocratique et de contribuer au bien commun.",
        "Ainsi, l'éducation ne représente pas seulement un droit fondamental : elle constitue aussi un puissant levier de transformation sociale.",
      ],
      missionVariantes: [
        "comme un podcast intellectuel et engagé",
        "avec un ton clair et structuré",
        "comme si tu défendais une cause importante",
        "avec gravité, en marquant les inégalités",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Société & Éducation',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour du rôle de l'éducation dans la société moderne. Lis, analyse et réagis comme dans un vrai débat sur les valeurs et l'avenir.",
        steps: [
          "analyser les idées du texte",
          "exprimer ton opinion personnelle",
          "discuter du rôle de l'école dans la société",
          "comparer différentes réalités éducatives",
          "défendre un point de vue",
          "réagir comme dans une vraie discussion",
        ],
        objectif: "Développer une expression plus structurée, plus analytique et plus argumentée en français.",
        temps: 'Lecture : 1 à 2 min · Réflexion & discussion : 4 à 6 min',
        conseil: "Développe tes idées comme dans une vraie discussion intellectuelle. N'hésite pas à nuancer, comparer et donner des exemples concrets tirés de ton expérience ou de l'actualité.",
      },
      speakingStarters: [
        "Selon moi…",
        "Cette idée met en évidence…",
        "On peut constater que…",
        "Il est essentiel de…",
        "Pourtant…",
        "En réalité…",
        "Cette situation soulève un débat important…",
        "L'éducation permet non seulement de…, mais aussi de…",
        "À long terme…",
        "D'un point de vue social…",
        "Il faudrait également prendre en compte…",
      ],
      reagis: [
        "Quelle vision de l'éducation est défendue dans le texte ?",
        "Pourquoi l'accès à une éducation de qualité est-il essentiel ?",
        "Quelles valeurs l'éducation transmet-elle, selon l'auteur ?",
        "Que signifie « investir dans l'éducation, c'est investir dans l'avenir » ?",
        "Pourquoi parle-t-on de « levier de transformation sociale » ?",
        "Quel lien le texte établit-il entre éducation et démocratie ?",
      ],
      opinionsQuestions: [
        "L'école d'aujourd'hui prépare-t-elle vraiment les élèves au monde réel ?",
        "Quelle valeur l'école devrait transmettre en priorité selon toi ?",
        "Les inégalités scolaires sont-elles une fatalité ou un choix politique ?",
        "L'esprit critique peut-il vraiment s'enseigner en classe ?",
        "Que faudrait-il changer en premier dans le système éducatif ?",
      ],
      reformulation: {
        instruction: "Reformule le texte comme une chronique de podcast intellectuel. Présente la thèse, les enjeux et ta propre vision de l'éducation. Parle pendant 2 à 3 minutes.",
        starters: ["Dans cet épisode, on s'intéresse à…", "L'idée centrale ici est que…", "Ce qui me frappe surtout, c'est…", "Personnellement, je pense que…"],
      },
      vocabulaire: [
        { expression: 'réduire les inégalités', context: "L'accès à une éducation de qualité est essentiel pour réduire les inégalités." },
        { expression: "développer l'esprit critique", context: "L'éducation permet de développer l'esprit critique." },
        { expression: 'favoriser la cohésion sociale', context: "Elle contribue à favoriser la cohésion sociale." },
        { expression: 'un droit fondamental', context: "L'éducation est un droit fondamental." },
        { expression: 'transmettre des valeurs', context: "L'école transmet des valeurs essentielles." },
        { expression: 'lutter contre les discriminations', context: "Elle permet de lutter contre les discriminations." },
        { expression: 'participer à la vie démocratique', context: "Une population éduquée participe activement à la vie démocratique." },
        { expression: 'un levier de transformation sociale', context: "L'éducation est un puissant levier de transformation sociale." },
      ],
      transformations: [
        {
          phrase: "L'éducation ne se limite pas à l'apprentissage de connaissances académiques.",
          instruction: "Reformule cette idée avec un vocabulaire B2 plus engagé.",
          exemple: "Réduire l'éducation à un simple transfert de savoirs serait passer à côté de sa véritable mission sociale.",
        },
        {
          phrase: "Investir dans l'éducation, c'est investir dans l'avenir.",
          instruction: "Développe cette idée avec un argument concret.",
          exemple: "Chaque euro investi dans l'éducation aujourd'hui réduit demain les coûts liés à la pauvreté, au chômage et à l'exclusion.",
        },
        {
          phrase: "L'éducation est un puissant levier de transformation sociale.",
          instruction: "Propose une illustration historique ou actuelle.",
          exemple: "L'alphabétisation massive du XXᵉ siècle a permis l'émancipation économique de millions de femmes dans le monde.",
        },
      ],
      dialogues: [
        { question: "Penses-tu que les diplômes ont encore la même valeur qu'avant ?", instruction: "Nuance ta réponse avec des exemples concrets." },
        { question: "Comment ferais-tu pour réduire les inégalités scolaires si tu étais ministre de l'éducation ?", instruction: "Propose deux ou trois mesures argumentées." },
        { question: "L'école devrait-elle apprendre à mieux vivre, ou seulement à mieux travailler ?", instruction: "Défends ta position avec une vraie réflexion personnelle." },
      ],
      reflexionFinale: "Et si la vraie révolution de notre époque, c'était de réinventer ce que l'on enseigne à nos enfants ?",
      defiDuration: '3 minutes',
      defiSujet1: "ton expérience personnelle de l'éducation",
      defiSujet2: "une réforme que tu mettrais en place demain",
      defiSujet3: "le rôle de l'éducation face aux défis du XXIᵉ siècle",
      defiConnecteurs: ["tout d'abord", "par ailleurs", "cela dit", "d'un point de vue social", "en définitive"],
    },
    {
      id: 'bonheur-simplicite',
      title: 'Le bonheur dans la simplicité : une réponse à l\'excès moderne',
      level: 'B2',
      missionExpressive: "Lis le texte à voix haute avec un ton calme, réfléchi et authentique. Imagine que tu participes à une émission qui explore les modes de vie modernes, le stress quotidien et la quête du bonheur. Transmets les nuances du texte, utilise des pauses naturelles et mets en valeur les oppositions d'idées avec une voix expressive et posée.",
      texte: [
        "Dans une société dominée par la vitesse, la performance et la consommation, le bonheur semble souvent devenir une quête inaccessible.",
        "Nous vivons dans un monde où l'on associe fréquemment la réussite à la possession matérielle et à l'accumulation.",
        "Pourtant, de nombreuses personnes réalisent que le véritable bonheur ne se trouve pas dans l'excès, mais dans la simplicité du quotidien.",
        "Un moment partagé, une promenade, une conversation sincère ou encore le calme intérieur peuvent apporter un sentiment de satisfaction bien plus durable que les biens matériels.",
        "À force de vouloir toujours plus, certains finissent par ressentir un profond vide émotionnel.",
        "La recherche constante de perfection et de réussite crée parfois davantage de frustration que de bonheur.",
        "Apprendre à ralentir, à apprécier le moment présent et à accepter l'imperfection pourrait alors représenter une forme de liberté.",
        "Le bonheur ne dépend pas uniquement des circonstances extérieures, mais aussi du regard que l'on porte sur sa propre vie.",
        "En fin de compte, la simplicité n'est peut-être pas un manque, mais une richesse souvent oubliée dans le monde moderne.",
      ],
      missionVariantes: [
        "comme un podcast introspectif",
        "avec un ton apaisé et lucide",
        "comme si tu confiais une réflexion intime",
        "avec calme, en marquant les paradoxes",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Société & Recherche du bonheur',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour du bonheur, de la société moderne et de notre rapport à la consommation. Lis, analyse et réagis comme dans une vraie émission introspective.",
        steps: [
          "analyser les idées du texte",
          "exprimer ton opinion personnelle",
          "discuter de la société moderne",
          "réfléchir à la notion de bonheur",
          "défendre ou nuancer ton point de vue",
          "réagir comme dans une vraie discussion",
        ],
        objectif: "Développer une expression plus naturelle, plus profonde et plus nuancée en français.",
        temps: 'Lecture : 1 à 2 min · Discussion & réflexion : 4 à 6 min',
        conseil: "Construis une réflexion personnelle et nuancée. Le plus intéressant n'est pas seulement ton opinion, mais la manière dont tu développes et expliques tes idées.",
      },
      speakingStarters: [
        "À mon avis…",
        "Cette idée reflète…",
        "On a souvent tendance à…",
        "Pourtant…",
        "Il est vrai que…",
        "Cela montre que…",
        "Cette situation révèle un paradoxe…",
        "Le problème vient peut-être du fait que…",
        "D'un point de vue personnel…",
        "Finalement…",
        "On peut se demander si…",
      ],
      reagis: [
        "Quel paradoxe central est mis en évidence dans le texte ?",
        "Comment notre société associe-t-elle souvent la réussite au bonheur ?",
        "Pourquoi l'accumulation peut-elle créer un vide émotionnel ?",
        "Quels exemples de bonheur simple sont donnés dans le texte ?",
        "Que signifie « accepter l'imperfection » selon l'auteur ?",
        "Pourquoi la simplicité est-elle présentée comme une richesse oubliée ?",
      ],
      opinionsQuestions: [
        "Penses-tu que notre société associe trop souvent bonheur et possession ?",
        "Quel est, pour toi, un vrai moment de bonheur simple ?",
        "Es-tu capable de ralentir dans ton quotidien ? Pourquoi ?",
        "La quête de perfection est-elle un piège dans le monde moderne ?",
        "Le bonheur dépend-il davantage de soi-même ou des circonstances extérieures ?",
      ],
      reformulation: {
        instruction: "Reformule le texte comme une chronique de podcast introspectif. Présente le paradoxe, les conséquences et ta propre vision du bonheur. Parle pendant 2 à 3 minutes.",
        starters: ["Dans cet épisode, on questionne…", "Le paradoxe est saisissant…", "Ce que je retiens surtout, c'est…", "Au fond, le vrai bonheur se trouve peut-être…"],
      },
      vocabulaire: [
        { expression: 'la quête du bonheur', context: "Le bonheur devient souvent une quête inaccessible." },
        { expression: 'la société de consommation', context: "Nous vivons dans une société de consommation et d'accumulation." },
        { expression: 'ressentir un vide émotionnel', context: "À force de vouloir toujours plus, certains finissent par ressentir un vide émotionnel." },
        { expression: 'apprécier le moment présent', context: "Apprendre à apprécier le moment présent est une forme de liberté." },
        { expression: "accepter l'imperfection", context: "Accepter l'imperfection libère du poids de la perfection." },
        { expression: 'ralentir le rythme', context: "Apprendre à ralentir change notre rapport au temps." },
        { expression: 'une satisfaction durable', context: "Un moment partagé apporte une satisfaction plus durable que les biens matériels." },
        { expression: "le regard que l'on porte sur la vie", context: "Le bonheur dépend aussi du regard que l'on porte sur sa propre vie." },
      ],
      transformations: [
        {
          phrase: "Le bonheur semble souvent devenir une quête inaccessible.",
          instruction: "Reformule cette idée avec un vocabulaire B2 plus nuancé.",
          exemple: "Le bonheur se transforme parfois en mirage, sans cesse repoussé par nos exigences modernes.",
        },
        {
          phrase: "Apprendre à ralentir, à apprécier le moment présent et à accepter l'imperfection pourrait représenter une forme de liberté.",
          instruction: "Développe cette idée avec une nuance personnelle.",
          exemple: "Ralentir, c'est se rappeler que la vie n'a pas besoin d'être performante pour avoir du sens.",
        },
        {
          phrase: "La simplicité n'est peut-être pas un manque, mais une richesse souvent oubliée.",
          instruction: "Propose une illustration concrète et personnelle.",
          exemple: "Un dîner sans téléphone, autour d'une table, peut soudain valoir plus qu'un voyage spectaculaire mal vécu.",
        },
      ],
      dialogues: [
        { question: "Que dirais-tu à quelqu'un qui pense que « plus on possède, plus on est heureux » ?", instruction: "Construis une réponse argumentée et empathique." },
        { question: "As-tu déjà vécu un moment où la simplicité t'a rendu(e) profondément heureux(se) ?", instruction: "Raconte avec des détails sensoriels et émotionnels." },
        { question: "La société moderne nous pousse-t-elle vraiment au malheur ?", instruction: "Nuance ta réponse avec des exemples concrets." },
      ],
      reflexionFinale: "Et si vivre mieux, c'était simplement apprendre à vouloir moins ?",
      defiDuration: '3 minutes',
      defiSujet1: "ta définition personnelle du bonheur",
      defiSujet2: "un excès moderne dont tu aimerais te libérer",
      defiSujet3: "un moment simple qui t'a rendu(e) profondément heureux(se)",
      defiConnecteurs: ["pour commencer", "d'un côté… de l'autre", "cela dit", "personnellement", "en définitive"],
    },
    {
      id: 'ogm',
      title: 'Les OGM : innovation scientifique ou risque pour l\'avenir ?',
      level: 'B2',
      missionExpressive: "Lis le texte à voix haute avec un ton sérieux, réfléchi et analytique. Imagine que tu participes à une émission où des experts débattent des liens entre science, environnement, économie et éthique. Mets en valeur les oppositions d'idées, marque les arguments importants, utilise des pauses naturelles et transmets le caractère complexe du sujet avec fluidité et nuance.",
      texte: [
        "Depuis plusieurs années, les organismes génétiquement modifiés suscitent un débat intense au sein de la société.",
        "Pour certains scientifiques et industriels, les OGM représentent une avancée majeure capable de répondre aux défis alimentaires mondiaux.",
        "En modifiant génétiquement certaines plantes, il devient possible d'augmenter les rendements agricoles, de limiter les pertes liées aux maladies et de réduire l'utilisation de certains pesticides.",
        "Dans un contexte de croissance démographique et de changement climatique, ces innovations apparaissent pour certains comme une nécessité.",
        "Cependant, de nombreuses voix s'élèvent pour dénoncer les risques potentiels liés à ces manipulations génétiques.",
        "Les effets à long terme sur la santé humaine et sur la biodiversité restent encore difficiles à mesurer avec précision.",
        "Par ailleurs, l'introduction d'espèces modifiées dans la nature pourrait perturber les équilibres écologiques existants.",
        "Au-delà des enjeux scientifiques, la question des OGM soulève également des préoccupations économiques et éthiques.",
        "Les grandes multinationales de l'agroalimentaire détiennent souvent le contrôle des semences brevetées, ce qui crée une dépendance inquiétante pour de nombreux agriculteurs.",
        "Cette concentration du pouvoir menace l'autonomie alimentaire de certains pays et accentue les inégalités.",
        "Face à cette situation complexe, il semble essentiel d'adopter une approche prudente et équilibrée.",
        "La recherche scientifique peut apporter des solutions précieuses, mais elle doit rester encadrée par des règles strictes et une réflexion éthique approfondie.",
        "Le progrès scientifique ne peut être durable que s'il respecte à la fois l'être humain, l'environnement et les générations futures.",
      ],
      missionVariantes: [
        "comme un podcast scientifique de débat",
        "avec un ton analytique et nuancé",
        "comme si tu opposais deux points de vue d'experts",
        "avec gravité, en marquant les enjeux éthiques",
      ],
      b2Intro: {
        title: '🎙️ Mode Podcast — Science, Éthique & Société',
        description: "Aujourd'hui, tu participes à une discussion de type podcast autour d'un sujet particulièrement controversé : les organismes génétiquement modifiés (OGM). Lis, analyse et réagis comme dans une vraie émission où experts et citoyens confrontent leurs idées.",
        steps: [
          "analyser les arguments du texte",
          "exprimer et défendre ton opinion",
          "discuter des avantages et des risques",
          "nuancer ton point de vue",
          "réagir aux contre-arguments comme dans un débat",
          "réfléchir aux conséquences scientifiques, économiques et éthiques",
        ],
        objectif: "Développer une expression orale plus structurée, plus nuancée et plus argumentative en français.",
        temps: 'Lecture : 1 à 2 min · Analyse & débat : 5 à 7 min',
        conseil: "Il ne s'agit pas seulement de donner ton opinion. Analyse différentes perspectives, nuance tes arguments, explore les conséquences possibles et construis une réflexion claire et structurée. Parle comme dans une vraie discussion de société ou un débat radio.",
      },
      speakingStarters: [
        "À première vue…",
        "D'un côté…, mais de l'autre…",
        "Cette question soulève un débat important…",
        "Il est difficile de nier que…",
        "Pourtant…",
        "Cependant…",
        "On peut également s'interroger sur…",
        "Les conséquences à long terme pourraient…",
        "Cette situation pose un problème éthique…",
        "Il faudrait trouver un équilibre entre…",
        "Certains estiment que…, tandis que d'autres pensent que…",
        "D'un point de vue scientifique…",
        "En fin de compte…",
      ],
      reagis: [
        "Quels sont les principaux arguments en faveur des OGM dans le texte ?",
        "Pourquoi certains scientifiques considèrent-ils les OGM comme une nécessité ?",
        "Quels risques pour la santé et la biodiversité sont évoqués ?",
        "En quoi la question des OGM est-elle aussi économique et éthique ?",
        "Quel rôle jouent les multinationales selon l'auteur ?",
        "Quelle approche le texte propose-t-il pour encadrer la recherche ?",
      ],
      opinionsQuestions: [
        "Es-tu plutôt pour ou contre les OGM ? Pourquoi ?",
        "Penses-tu que la science doit avoir des limites éthiques ?",
        "Qui devrait décider de l'usage des OGM : les scientifiques, les États ou les citoyens ?",
        "Le progrès agricole justifie-t-il certains risques pour l'environnement ?",
        "Comment garantir l'autonomie alimentaire des pays les plus fragiles ?",
      ],
      reformulation: {
        instruction: "Reformule le texte comme une chronique de podcast scientifique. Présente les arguments pour, les arguments contre, et propose une position personnelle nuancée. Parle pendant 2 à 3 minutes.",
        starters: ["Dans cet épisode, on aborde un sujet brûlant…", "Le débat se cristallise autour de…", "Ce que je retiens surtout, c'est que…", "Au fond, la vraie question est…"],
      },
      vocabulaire: [
        { expression: 'les organismes génétiquement modifiés', context: "Les OGM suscitent un débat intense au sein de la société." },
        { expression: 'une avancée scientifique', context: "Pour certains, les OGM représentent une avancée scientifique majeure." },
        { expression: 'les rendements agricoles', context: "Ces innovations permettent d'augmenter les rendements agricoles." },
        { expression: 'les conséquences à long terme', context: "Les conséquences à long terme restent difficiles à mesurer." },
        { expression: 'la biodiversité', context: "Les effets sur la biodiversité inquiètent de nombreux scientifiques." },
        { expression: 'les équilibres écologiques', context: "L'introduction d'OGM pourrait perturber les équilibres écologiques." },
        { expression: 'les multinationales agroalimentaires', context: "Les multinationales agroalimentaires contrôlent les semences brevetées." },
        { expression: 'la souveraineté alimentaire', context: "Cette dépendance menace la souveraineté alimentaire de nombreux pays." },
        { expression: 'une réflexion éthique', context: "La recherche doit s'accompagner d'une réflexion éthique approfondie." },
        { expression: 'encadrer la recherche scientifique', context: "Il est essentiel d'encadrer la recherche scientifique par des règles strictes." },
      ],
      transformations: [
        {
          phrase: "Les OGM représentent une avancée majeure capable de répondre aux défis alimentaires mondiaux.",
          instruction: "Reformule cette idée avec un vocabulaire plus critique.",
          exemple: "Présenter les OGM comme la seule réponse aux enjeux alimentaires masque souvent les intérêts économiques en jeu.",
        },
        {
          phrase: "Les effets à long terme sur la santé humaine et sur la biodiversité restent encore difficiles à mesurer.",
          instruction: "Développe cette idée avec une nuance scientifique.",
          exemple: "Tant que la communauté scientifique n'a pas de recul sur plusieurs décennies, toute conclusion définitive reste prématurée.",
        },
        {
          phrase: "Cette concentration du pouvoir menace l'autonomie alimentaire de certains pays.",
          instruction: "Propose une illustration concrète ou géopolitique.",
          exemple: "Dans certains pays du Sud, les agriculteurs dépendent désormais entièrement de quelques multinationales pour leurs semences chaque saison.",
        },
      ],
      dialogues: [
        { question: "Mangerais-tu des aliments OGM en toute confiance ? Pourquoi ?", instruction: "Construis une réponse argumentée et nuancée." },
        { question: "Penses-tu qu'il soit possible de séparer science et intérêts économiques ?", instruction: "Défends ta position avec des exemples concrets." },
        { question: "Comment réagirais-tu face à un proche convaincu que « les OGM sauveront le monde » ?", instruction: "Confronte ses arguments avec respect, mais fermeté." },
      ],
      reflexionFinale: "Et si la vraie question n'était pas « faut-il accepter les OGM ? », mais « à qui appartient le vivant ? »",
      defiDuration: '3 minutes',
      defiSujet1: "ta position personnelle sur les OGM",
      defiSujet2: "le rôle de la science face aux défis du XXIᵉ siècle",
      defiSujet3: "le pouvoir des multinationales sur notre alimentation",
      defiConnecteurs: ["d'un côté", "de l'autre", "cela dit", "d'un point de vue éthique", "en définitive"],
    },
  ],
};

function TextActivity({ text }) {
  return (
    <div className="space-y-4" data-testid={`text-${text.id}`}>
      {/* B2 Intro Card */}
      {text.b2Intro && (
        <div className="bg-[#1A2F4A] rounded-2xl p-6 space-y-4" data-testid={`b2intro-${text.id}`}>
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">{text.b2Intro.title}</p>
          <p className="text-sm text-white/80 leading-relaxed">{text.b2Intro.description}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {text.b2Intro.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-[10px] text-[#D4AF37] mt-0.5">&#x25B8;</span>
                <span className="text-xs text-white/60">{s}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-white/10">
            <div className="flex-1 bg-white/5 rounded-lg px-3 py-2">
              <p className="text-[9px] text-[#D4AF37] uppercase tracking-wider font-bold">Objectif</p>
              <p className="text-[11px] text-white/70 mt-0.5">{text.b2Intro.objectif}</p>
            </div>
            <div className="flex-1 bg-white/5 rounded-lg px-3 py-2">
              <p className="text-[9px] text-[#D4AF37] uppercase tracking-wider font-bold">Temps</p>
              <p className="text-[11px] text-white/70 mt-0.5">{text.b2Intro.temps}</p>
            </div>
          </div>
          <div className="bg-white/5 rounded-lg px-4 py-3">
            <p className="text-xs text-white/50 italic leading-relaxed">{text.b2Intro.conseil}</p>
          </div>
        </div>
      )}

      {/* Étape 1 — Lecture expressive */}
      <CollapsibleSection icon={BookOpen} title="Étape 1 — Lecture expressive" accent="#3B82F6" testId={`etape1-${text.id}`} defaultOpen>
        <div className="bg-[#FAF7F0] rounded-xl p-5 space-y-3">
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Mission</p>
          <p className="text-sm text-[#1A2F4A] italic">{text.missionExpressive}</p>
        </div>

        <div className="bg-[#1A2F4A] rounded-xl p-5 space-y-2" data-testid={`texte-${text.id}`}>
          {text.texte.map((line, i) => (
            <p key={i} className="text-sm text-white/90 leading-relaxed">{line}</p>
          ))}
        </div>

        <RecordButton testId={`record-lecture-${text.id}`} label="Lis à voix haute" />

        {text.missionVariantes.length > 0 && (
          <div className="bg-white border border-[#E8E4DC] rounded-xl p-4 space-y-2">
            <p className="text-xs text-[#1A2F4A] font-bold">Maintenant, relis le texte :</p>
            <div className="flex flex-wrap gap-2">
              {text.missionVariantes.map((v, i) => (
                <span key={i} className="text-xs bg-[#D4AF37]/10 text-[#6B4E37] rounded-full px-3 py-1 font-medium">{v}</span>
              ))}
            </div>
            <RecordButton testId={`record-variante-${text.id}`} label="Enregistrer une variante" />
          </div>
        )}
      </CollapsibleSection>

      {/* Étape 2 — Réagis */}
      <CollapsibleSection icon={MessageCircle} title="Étape 2 — Réagis au texte" accent="#10B981" testId={`etape2-${text.id}`}>
        <p className="text-xs text-[#6B4E37] italic">Réponds oralement avec des phrases simples.</p>
        <div className="space-y-2">
          {text.reagis.map((q, i) => (
            <div key={i} className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-lg px-4 py-3 flex items-start gap-3" data-testid={`question-${text.id}-${i}`}>
              <span className="text-xs text-[#D4AF37] font-bold mt-0.5">{i + 1}.</span>
              <div className="flex-1">
                <p className="text-sm text-[#1A2F4A]">{q}</p>
              </div>
            </div>
          ))}
        </div>
        <RecordButton testId={`record-reagis-${text.id}`} label="Enregistrer mes réponses" />
      </CollapsibleSection>

      {/* Étape — Réagis avec ton opinion (A2+) */}
      {text.opinionsQuestions && text.opinionsQuestions.length > 0 && (
        <CollapsibleSection icon={Lightbulb} title="Réagis avec ton opinion" accent="#F97316" testId={`opinions-${text.id}`}>
          <p className="text-xs text-[#6B4E37] italic">Réponds librement et donne une petite explication.</p>
          <div className="space-y-2">
            {text.opinionsQuestions.map((q, i) => (
              <div key={i} className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-lg px-4 py-3 flex items-start gap-3" data-testid={`opinion-${text.id}-${i}`}>
                <span className="text-xs text-[#F97316] font-bold mt-0.5">{i + 1}.</span>
                <p className="text-sm text-[#1A2F4A]">{q}</p>
              </div>
            ))}
          </div>
          <RecordButton testId={`record-opinions-${text.id}`} label="Enregistrer mes opinions" />
        </CollapsibleSection>
      )}

      {/* B2 — Speaking Starters */}
      {text.speakingStarters && text.speakingStarters.length > 0 && (
        <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-5 space-y-3" data-testid={`starters-${text.id}`}>
          <p className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold">Pour t'aider à démarrer</p>
          <div className="flex flex-wrap gap-1.5">
            {text.speakingStarters.map((s, i) => (
              <span key={i} className="text-[11px] bg-white border border-[#E8E4DC] text-[#1A2F4A] rounded-full px-3 py-1.5 font-medium italic">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Étape — Transforme */}
      <CollapsibleSection icon={RefreshCw} title="Étape 3 — Transforme les phrases" accent="#F59E0B" testId={`etape3-${text.id}`}>
        <p className="text-xs text-[#6B4E37] italic">Lis la phrase puis change une information.</p>
        <div className="space-y-3">
          {text.transformations.map((t, i) => (
            <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl p-4 space-y-2" data-testid={`transform-${text.id}-${i}`}>
              <div className="bg-[#1A2F4A] rounded-lg px-4 py-2.5">
                <p className="text-sm text-white/90 italic">"{t.phrase}"</p>
              </div>
              <p className="text-xs text-[#D4AF37] font-bold">{t.instruction}</p>
              <div className="bg-[#FAF7F0] rounded-lg px-4 py-2.5 border border-[#E8E4DC]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-1">Exemple possible</p>
                <p className="text-sm text-[#1A2F4A] italic">"{t.exemple}"</p>
              </div>
            </div>
          ))}
        </div>
        <RecordButton testId={`record-transform-${text.id}`} label="Enregistrer mes transformations" />
      </CollapsibleSection>

      {/* Étape 4 — Dialogue */}
      <CollapsibleSection icon={Users} title="Étape 4 — Passe au dialogue" accent="#8B5CF6" testId={`etape4-${text.id}`}>
        <p className="text-xs text-[#6B4E37] italic">Imagine qu'un ami te pose des questions. Réponds naturellement.</p>
        <div className="space-y-3">
          {text.dialogues.map((d, i) => (
            <div key={i} className="bg-white border border-[#E8E4DC] rounded-xl p-4 space-y-3" data-testid={`dialogue-${text.id}-${i}`}>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-[#1A2F4A] text-white flex items-center justify-center flex-shrink-0">
                  <Users className="w-3 h-3" />
                </div>
                <p className="text-sm text-[#1A2F4A] font-medium pt-1">{d.question}</p>
              </div>
              <div className="flex items-start gap-3 ml-10">
                <p className="text-xs text-[#999] italic">{d.instruction}</p>
              </div>
              <div className="ml-10">
                <RecordButton testId={`record-dialogue-${text.id}-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* B1+ — Reformulation */}
      {text.reformulation && (
        <CollapsibleSection icon={RefreshCw} title="Reformulation" accent="#06B6D4" testId={`reformulation-${text.id}`}>
          <p className="text-xs text-[#6B4E37] italic">{text.reformulation.instruction}</p>
          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl p-4">
            <p className="text-[10px] text-[#999] uppercase tracking-wider font-bold mb-2">Pour t'aider à démarrer</p>
            <div className="flex flex-wrap gap-1.5">
              {text.reformulation.starters.map((s, i) => (
                <span key={i} className="text-xs bg-[#1A2F4A]/5 text-[#1A2F4A] rounded-full px-3 py-1 font-medium italic">"{s}"</span>
              ))}
            </div>
          </div>
          <RecordButton testId={`record-reformulation-${text.id}`} label="Enregistrer ma reformulation" />
        </CollapsibleSection>
      )}

      {/* B1+ — Vocabulaire actif */}
      {text.vocabulaire && text.vocabulaire.length > 0 && (
        <CollapsibleSection icon={BookOpen} title="Vocabulaire actif" accent="#8B5CF6" testId={`vocabulaire-${text.id}`}>
          <p className="text-xs text-[#6B4E37] italic">Expressions utiles du texte. Réutilise-en une dans une phrase personnelle.</p>
          <div className="space-y-2">
            {text.vocabulaire.map((v, i) => (
              <div key={i} className="bg-white border border-[#E8E4DC] rounded-lg px-4 py-3" data-testid={`vocab-${text.id}-${i}`}>
                <p className="text-sm font-bold text-[#1A2F4A]">{v.expression}</p>
                <p className="text-xs text-[#999] italic mt-1">"{v.context}"</p>
              </div>
            ))}
          </div>
          <RecordButton testId={`record-vocabulaire-${text.id}`} label="Utilise une expression dans une phrase" />
        </CollapsibleSection>
      )}

      {/* B1+ — Réflexion finale */}
      {text.reflexionFinale && (
        <div className="bg-[#1A2F4A] rounded-xl p-5 space-y-3" data-testid={`reflexion-${text.id}`}>
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold">Réflexion finale</p>
          <p className="text-sm text-white/90 italic leading-relaxed">"{text.reflexionFinale}"</p>
          <RecordButton testId={`record-reflexion-${text.id}`} label="Partager ma réflexion" />
        </div>
      )}
    </div>
  );
}

const LEVELS = ['A1', 'A2', 'B1', 'B2'];

export default function LisEtReagis() {
  const [activeLevel, setActiveLevel] = useState('A1');
  const [activeText, setActiveText] = useState(0);
  const texts = TEXTS[activeLevel] || [];
  const currentText = texts[activeText] || texts[0];

  const handleLevelChange = (lvl) => {
    setActiveLevel(lvl);
    setActiveText(0);
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      <section className="pt-16 pb-8">
        <div className="max-w-[560px] mx-auto px-8">
          <Link to="/speaking-lab" className="inline-flex items-center gap-1 text-xs text-[#999] hover:text-[#D4AF37] transition-colors mb-6" data-testid="back-to-lab">
            <ArrowRight className="w-3 h-3 rotate-180" /> Atelier d'Expression
          </Link>
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
              Lis & Réagis
            </h1>
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#D4AF37]/50" />
              <BookOpen className="w-4 h-4 text-[#D4AF37]/60" />
              <div className="h-px w-8 bg-[#D4AF37]/50" />
            </div>
            <p className="text-xs text-[#6B4E37] italic">
              Lis, ressens, réagis — transforme chaque texte en conversation vivante.
            </p>
          </div>

          {/* Level tabs */}
          <div className="flex gap-2 justify-center mb-4" data-testid="level-tabs">
            {LEVELS.map(lvl => (
              <button
                key={lvl}
                onClick={() => handleLevelChange(lvl)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  activeLevel === lvl
                    ? 'bg-[#1A2F4A] text-white'
                    : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                }`}
                data-testid={`level-btn-${lvl}`}>
                {lvl}
              </button>
            ))}
          </div>

          {/* Text selector */}
          <div className="flex gap-2 justify-center mb-6 flex-wrap" data-testid="text-selector">
            {texts.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setActiveText(i)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  activeText === i
                    ? 'bg-[#D4AF37] text-white'
                    : 'bg-white border border-[#E8E4DC] text-[#1A2F4A] hover:border-[#D4AF37]'
                }`}
                data-testid={`text-btn-${t.id}`}>
                {t.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-[560px] mx-auto px-8">
          <TextActivity text={currentText} />
        </div>
      </section>

      {/* Défi final */}
      <section className="pb-20">
        <div className="max-w-[560px] mx-auto px-8">
          <div className="bg-[#1A2F4A] rounded-2xl p-6 text-center space-y-4" data-testid="defi-final">
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              <p className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Petit défi final</p>
            </div>
            <p className="text-xs text-white/70 leading-relaxed max-w-xs mx-auto">
              Parle pendant <span className="text-[#D4AF37] font-bold">{currentText.defiDuration || '30 secondes'}</span> sur :
            </p>
            <div className="flex flex-col items-center gap-1.5">
              <span className="text-xs text-[#D4AF37] font-bold">{currentText.defiSujet1 || 'ta famille'}</span>
              <span className="text-[10px] text-white/40">ou</span>
              <span className="text-xs text-[#D4AF37] font-bold">{currentText.defiSujet2 || 'ton meilleur ami'}</span>
              {currentText.defiSujet3 && (
                <>
                  <span className="text-[10px] text-white/40">ou</span>
                  <span className="text-xs text-[#D4AF37] font-bold">{currentText.defiSujet3}</span>
                </>
              )}
            </div>
            {currentText.defiConnecteurs && (
              <div className="flex flex-wrap justify-center gap-1.5">
                {currentText.defiConnecteurs.map((c, i) => (
                  <span key={i} className="text-[10px] bg-white/10 text-white/60 rounded-full px-3 py-1">{c}</span>
                ))}
              </div>
            )}
            {!currentText.defiConnecteurs && (
              <div className="flex flex-wrap justify-center gap-2">
                <span className="text-[10px] bg-white/10 text-white/60 rounded-full px-3 py-1">phrases simples</span>
                <span className="text-[10px] bg-white/10 text-white/60 rounded-full px-3 py-1">émotions</span>
                <span className="text-[10px] bg-white/10 text-white/60 rounded-full px-3 py-1">détails personnels</span>
              </div>
            )}
            <p className="text-[10px] text-white/40 italic">Objectif : parler naturellement et avec confiance.</p>
            <RecordButton testId="record-defi-final" label="Relever le défi — 30 secondes" />
          </div>
        </div>
      </section>
    </div>
  );
}
